/**
 * MapboxAPI - Abstracts Mapbox GL JS operations for layer management
 * Handles rendering, updating, and removing different layer types on a Mapbox map
 */
import { LayerOrderManager } from './layer-order-manager.js';
import { DataUtils, GeoUtils } from './map-utils.js';
import { KMLConverter } from './kml-converter.js';
import { LayerConfigGenerator } from './layer-creator-ui.js';
import { OverpassLoader } from './overpass-loader.js';
import ConfigManager from './config-manager.js';
import { handlerLoader } from './inspection-handler-loader.js';
import * as GoogleSheetsAPI from './google-sheets-api.js';

const COG_PROVIDER_URL = new URL('./cog-tile-provider.js', import.meta.url).href;
let _cogProviderRegistered = false;
function registerCOGProvider() {
    if (_cogProviderRegistered) return;
    if (typeof mapboxgl === 'undefined' || typeof mapboxgl.addTileProvider !== 'function') return;
    mapboxgl.addTileProvider('cog', COG_PROVIDER_URL);
    _cogProviderRegistered = true;
}

export class MapboxAPI {
    constructor(map, atlasConfig = {}) {
        this._map = map;
        this._atlasConfig = atlasConfig;
        this._defaultStyles = atlasConfig.styles || {};
        this._orderedGroups = atlasConfig.orderedGroups || []; // Store ordered groups for layer positioning
        registerCOGProvider();
        this._layerCache = new Map(); // Cache for layer configurations
        this._sourceCache = new Map(); // Cache for sources
        this._refreshTimers = new Map(); // Cache for refresh timers
        this._blinkTimers = new Map(); // Cache for blink timers
        this._eventListeners = new Map(); // Cache for event listeners
        this._timeBasedLayers = new Map(); // Cache for layers with time parameters
        this._overpassLoaders = new Map(); // OverpassLoader instances keyed by groupId

        // Initialize style property mapping for different layer types
        this._stylePropertyMapping = this._initializeStylePropertyMapping();

        // Set up time change event listener
        this._setupTimeChangeListener();
    }

    /**
     * Set up time change event listener
     */
    _setupTimeChangeListener() {
        // Listen for time change events from TimeControl
        const timeChangeHandler = (event) => {
            const { selectedDate, isoString, urlFormat } = event.detail;
            this._updateTimeBasedLayers(urlFormat);
        };

        // Listen on both map container and window for maximum compatibility
        this._map.getContainer().addEventListener('timechange', timeChangeHandler);
        window.addEventListener('timechange', timeChangeHandler);

        // Store handler for cleanup
        this._timeChangeHandler = timeChangeHandler;
    }

    /**
     * Update all time-based layers with new time parameter
     * @param {string} timeString - ISO time string for URL parameters
     */
    _updateTimeBasedLayers(timeString) {

        this._timeBasedLayers.forEach((layerInfo, groupId) => {
            const { config, visible } = layerInfo;

            if (!visible) {
                return;
            }

            try {
                this._updateLayerTime(groupId, config, timeString);
            } catch (error) {
            }
        });
    }

    /**
     * Update a specific layer's time parameter
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @param {string} timeString - ISO time string
     */
    _updateLayerTime(groupId, config, timeString) {
        if (!config.urlTimeParam) {
            return;
        }

        // Generate new URL with time parameter
        const newUrl = this._generateTimeBasedUrl(config.url, config.urlTimeParam, timeString);

        // Update the source based on layer type
        switch (config.type) {
            case 'wmts':
                this._updateWMTSLayerTime(groupId, config, newUrl);
                break;
            case 'tms':
                this._updateTMSLayerTime(groupId, config, newUrl);
                break;
            case 'wms':
                this._updateWMSLayerTime(groupId, config, newUrl);
                break;
            case 'img':
                this._updateImageLayerTime(groupId, config, newUrl);
                break;
        }
    }

    /**
     * Generate a new URL with time parameter
     * @param {string} baseUrl - Original URL
     * @param {string} timeParam - Time parameter template (e.g., "TIME={time}")
     * @param {string} timeString - Time value to insert
     * @returns {string} Updated URL
     */
    _generateTimeBasedUrl(baseUrl, timeParam, timeString) {
        // Convert ISO string to YYYY-MM-DD format for GIBS layers
        let formattedTimeString = timeString;
        if (baseUrl.includes('gibs.earthdata.nasa.gov') || baseUrl.includes('earthdata.nasa.gov')) {
            // GIBS layers expect YYYY-MM-DD format
            const date = new Date(timeString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedTimeString = `${year}-${month}-${day}`;
        }

        // Replace the time placeholder in the timeParam with the formatted time
        const timeValue = timeParam.replace('{time}', formattedTimeString);

        // If the base URL already has the time parameter, replace it
        if (baseUrl.includes(timeParam.split('=')[0] + '=')) {
            // Find and replace existing time parameter
            const timeParamKey = timeParam.split('=')[0];
            const urlParts = baseUrl.split('&');
            const updatedParts = urlParts.map(part => {
                if (part.includes(timeParamKey + '=')) {
                    return timeValue;
                }
                return part;
            });
            return updatedParts.join('&');
        } else {
            // Add new time parameter
            const separator = baseUrl.includes('?') ? '&' : '?';
            return `${baseUrl}${separator}${timeValue}`;
        }
    }

    /**
     * Update WMTS layer with new time-based URL
     */
    _updateWMTSLayerTime(groupId, config, newUrl) {
        const sourceId = `wmts-${groupId}`;
        const source = this._map.getSource(sourceId);

        if (source) {
            // Store current config for URL conversion
            this._currentConfig = config;

            // Convert WMTS URL to XYZ tile format
            const tileUrl = this._convertWMTSToXYZ(newUrl);

            // Remove and re-add source with new URL
            const layerId = `wmts-layer-${groupId}`;
            if (this._map.getLayer(layerId)) {
                this._map.removeLayer(layerId);
            }
            this._map.removeSource(sourceId);

            // Add source with new URL
            const sourceConfig = {
                type: 'raster',
                tileSize: config.tileSize || 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22,
                tiles: [tileUrl]
            };

            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            // Re-add layer
            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible: true
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'wmts', null, config, this._orderedGroups));
            LayerOrderManager.logLayerStack(this._map, `After adding WMTS layer: ${config.id}`);

        }
    }

    /**
     * Update TMS layer with new time-based URL
     */
    _updateTMSLayerTime(groupId, config, newUrl) {
        const sourceId = `tms-${groupId}`;
        const source = this._map.getSource(sourceId);

        if (source) {
            // Remove and re-add source with new URL
            const layerId = `tms-layer-${groupId}`;
            if (this._map.getLayer(layerId)) {
                this._map.removeLayer(layerId);
            }
            this._map.removeSource(sourceId);

            let tileUrl = newUrl;

            // Wrap with proxy if configured
            if (config.proxyUrl) {
                const encodedTileUrl = encodeURIComponent(tileUrl)
                    .replace(/%7Bz%7D/g, '{z}')
                    .replace(/%7Bx%7D/g, '{x}')
                    .replace(/%7By%7D/g, '{y}');
                tileUrl = `${config.proxyUrl}?url=${encodedTileUrl}`;
                if (config.proxyReferer) {
                    const encodedReferer = encodeURIComponent(config.proxyReferer);
                    tileUrl += `&referer=${encodedReferer}`;
                }
            }

            // Add source with new URL
            const sourceConfig = {
                type: 'raster',
                tileSize: 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22,
                tiles: [tileUrl]
            };

            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            // Re-add layer
            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible: true
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'tms', null, config, this._orderedGroups));
            LayerOrderManager.logLayerStack(this._map, `After adding TMS layer: ${config.id}`);

        }
    }

    /**
     * Update Image layer with new time-based URL
     */
    _updateImageLayerTime(groupId, config, newUrl) {
        const source = this._map.getSource(groupId);

        if (source && source.updateImage) {
            // For image layers, update the image source
            const bounds = config.bounds || config.bbox;

            source.updateImage({
                url: newUrl,
                coordinates: [
                    [bounds[0], bounds[3]], // top-left
                    [bounds[2], bounds[3]], // top-right
                    [bounds[2], bounds[1]], // bottom-right
                    [bounds[0], bounds[1]]  // bottom-left
                ]
            });

        }
    }

    /**
     * Split a style object with optional "prefix/property" keys into ordered variants.
     *
     * Keys may be plain (e.g. "line-color") or prefixed (e.g. "overlay/line-color").
     * Each unique prefix becomes a separate variant that creates its own map layer
     * sharing the same source, allowing multi-pass cartography (e.g. casing + centerline).
     *
     * Order: keys are iterated in reverse so the first prefix encountered (= the last
     * one defined in the config) ends up on top. Mapbox renders later-added layers
     * on top, so we return variants in render order — index 0 is at the bottom.
     *
     * Example input:
     *   { "overlay/line-color": "white", "overlay/line-width": 2,
     *     "line-color": "purple", "line-width": 1 }
     *
     * Example output (rendered bottom → top):
     *   [{ prefix: "",        style: { "line-color": "purple", "line-width": 1 } },
     *    { prefix: "overlay", style: { "line-color": "white",  "line-width": 2 } }]
     */
    _parseStyleVariants(style) {
        if (!style || typeof style !== 'object') {
            return [{ prefix: '', style: {} }];
        }

        const order = []; // Order in which unique prefixes are encountered (reverse iter)
        const byPrefix = new Map(); // prefix → un-prefixed style object
        const keys = Object.keys(style);

        for (let i = keys.length - 1; i >= 0; i--) {
            const key = keys[i];
            const slashIdx = key.indexOf('/');
            const prefix = slashIdx > 0 ? key.substring(0, slashIdx) : '';
            const prop = slashIdx > 0 ? key.substring(slashIdx + 1) : key;

            if (!byPrefix.has(prefix)) {
                byPrefix.set(prefix, {});
                order.push(prefix);
            }
            byPrefix.get(prefix)[prop] = style[key];
        }

        return order.map(prefix => ({ prefix, style: byPrefix.get(prefix) }));
    }

    /**
     * Get the ID suffix used to disambiguate map layers for a style variant.
     * Base variant (empty prefix) returns '' so existing layer IDs are preserved.
     */
    _getVariantSuffix(prefix) {
        return prefix ? `--${prefix}` : '';
    }

    /**
     * Get all variant prefixes (including '' for base) declared in a config's style.
     * Returns ['' ] when the config has no style or no prefixed keys.
     */
    _getVariantPrefixes(config) {
        if (!config || !config.style) return [''];
        return this._parseStyleVariants(config.style).map(v => v.prefix);
    }

    /**
     * Initialize comprehensive mapping of Mapbox GL style properties
     */
    _initializeStylePropertyMapping() {
        return {
            layout: {
                common: ['visibility'],
                fill: ['fill-sort-key'],
                line: ['line-cap', 'line-join', 'line-miter-limit', 'line-round-limit', 'line-sort-key'],
                symbol: ['icon-allow-overlap', 'icon-anchor', 'icon-image', 'icon-size', 'text-field', 'text-font', 'text-size', 'text-anchor', 'text-line-height', 'text-max-width', 'text-justify', 'text-allow-overlap', 'text-transform', 'text-offset', 'text-rotation-alignment', 'text-pitch-alignment', 'text-writing-mode', 'text-variable-anchor', 'text-radial-offset', 'text-keep-upright', 'text-padding', 'symbol-placement', 'symbol-spacing', 'symbol-avoid-edges', 'icon-rotation-alignment', 'icon-pitch-alignment', 'icon-keep-upright'],
                circle: ['circle-sort-key'],
                raster: [],
                background: [],
                hillshade: []
            },
            paint: {
                fill: ['fill-color', 'fill-opacity', 'fill-outline-color', 'fill-translate'],
                'fill-extrusion': ['fill-extrusion-opacity', 'fill-extrusion-color', 'fill-extrusion-translate', 'fill-extrusion-height', 'fill-extrusion-base', 'fill-extrusion-vertical-gradient'],
                line: ['line-color', 'line-width', 'line-opacity', 'line-dasharray', 'line-translate', 'line-offset'],
                symbol: ['icon-color', 'icon-opacity', 'text-color', 'text-halo-color', 'text-halo-width', 'text-opacity'],
                circle: ['circle-radius', 'circle-color', 'circle-opacity', 'circle-stroke-width', 'circle-stroke-color'],
                raster: ['raster-opacity', 'raster-contrast', 'raster-saturation', 'raster-brightness-min', 'raster-brightness-max'],
                background: ['background-color', 'background-opacity'],
                hillshade: ['hillshade-exaggeration', 'hillshade-highlight-color', 'hillshade-shadow-color']
            }
        };
    }

    /**
     * Create a layer group on the map
     * @param {string} groupId - Unique identifier for the layer group
     * @param {Object} config - Layer configuration object
     * @param {Object} options - Additional options
     * @returns {Promise<boolean>} - Success status
     */
    async createLayerGroup(groupId, config, options = {}) {
        try {
            const { visible = false, currentGroup = null } = options;

            // Fill in fallback title/description/attribution derived from the
            // layer type and source URL. This ensures a minimal config (e.g. an
            // inline layer from the ?layers= URL param) still contributes
            // attribution to the source, so it appears in the attribution bar.
            config = ConfigManager.applyDefaultMetadata(config);

            // Register time-based layers
            if (config.urlTimeParam) {
                this._timeBasedLayers.set(groupId, { config, visible });
            }

            switch (config.type) {
                case 'style':
                    return this._createStyleLayer(groupId, config, visible);
                case 'vector':
                    return this._createVectorLayer(groupId, config, visible);
                case 'tms':
                    return this._createTMSLayer(groupId, config, visible);
                case 'wmts':
                    return this._createWMTSLayer(groupId, config, visible);
                case 'wms':
                    return this._createWMSLayer(groupId, config, visible);
                case 'cog':
                    return this._createCOGLayer(groupId, config, visible);
                case 'geojson':
                    return this._createGeoJSONLayer(groupId, config, visible);
                case 'js':
                    return this._createJsLayer(groupId, config, visible);
                case 'overpass':
                    return this._createOverpassLayer(groupId, config, visible);
                case 'csv':
                    return this._createCSVLayer(groupId, config, visible);
                case 'sheet':
                    return this._createSheetLayer(groupId, config, visible);
                case 'img':
                    return this._createImageLayer(groupId, config, visible);
                case 'raster-style-layer':
                    return this._createRasterStyleLayer(groupId, config, visible);

                case 'layer-group':
                    return this._createLayerGroupToggle(groupId, config, visible);
                default:
                    console.warn(`Unknown layer type: ${config.type}`);
                    return false;
            }
        } catch (error) {
            console.error(`Error creating layer group ${groupId}:`, error);
            return false;
        }
    }

    /**
     * Update layer group visibility
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @param {boolean} visible - Visibility state
     * @returns {boolean} - Success status
     */
    updateLayerGroupVisibility(groupId, config, visible) {
        try {
            // Update time-based layer visibility tracking
            if (config.urlTimeParam && this._timeBasedLayers.has(groupId)) {
                const layerInfo = this._timeBasedLayers.get(groupId);
                layerInfo.visible = visible;
                this._timeBasedLayers.set(groupId, layerInfo);
            }

            switch (config.type) {
                case 'style':
                    return this._updateStyleLayerVisibility(groupId, config, visible);
                case 'vector':
                    return this._updateVectorLayerVisibility(groupId, config, visible);
                case 'tms':
                    return this._updateTMSLayerVisibility(groupId, config, visible);
                case 'wmts':
                    return this._updateWMTSLayerVisibility(groupId, config, visible);
                case 'wms':
                    return this._updateWMSLayerVisibility(groupId, config, visible);
                case 'cog':
                    return this._updateCOGLayerVisibility(groupId, config, visible);
                case 'geojson':
                    return this._updateGeoJSONLayerVisibility(groupId, config, visible);
                case 'js':
                    return this._updateGeoJSONLayerVisibility(groupId, config, visible);
                case 'overpass':
                    return this._updateOverpassLayerVisibility(groupId, config, visible);
                case 'csv':
                case 'sheet':
                    return this._updateCSVLayerVisibility(groupId, config, visible);
                case 'img':
                    return this._updateImageLayerVisibility(groupId, config, visible);
                case 'raster-style-layer':
                    return this._updateRasterStyleLayerVisibility(groupId, config, visible);

                case 'layer-group':
                    return this._updateLayerGroupToggleVisibility(groupId, config, visible);
                default:
                    return false;
            }
        } catch (error) {
            console.error(`Error updating layer group visibility ${groupId}:`, error);
            return false;
        }
    }

    /**
     * Remove a layer group from the map
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @returns {boolean} - Success status
     */
    removeLayerGroup(groupId, config) {
        try {
            // Clear any refresh timers
            if (this._refreshTimers.has(groupId)) {
                clearInterval(this._refreshTimers.get(groupId));
                this._refreshTimers.delete(groupId);
            }

            // Remove from time-based layers tracking
            if (this._timeBasedLayers.has(groupId)) {
                this._timeBasedLayers.delete(groupId);
            }

            switch (config.type) {
                case 'style':
                    return this._removeStyleLayer(groupId, config);
                case 'vector':
                    return this._removeVectorLayer(groupId, config);
                case 'tms':
                    return this._removeTMSLayer(groupId, config);
                case 'wmts':
                    return this._removeWMTSLayer(groupId, config);
                case 'wms':
                    return this._removeWMSLayer(groupId, config);
                case 'cog':
                    return this._removeCOGLayer(groupId, config);
                case 'geojson':
                    return this._removeGeoJSONLayer(groupId, config);
                case 'js':
                    return this._removeGeoJSONLayer(groupId, config);
                case 'overpass':
                    return this._removeOverpassLayer(groupId, config);
                case 'csv':
                case 'sheet':
                    return this._removeCSVLayer(groupId, config);
                case 'img':
                    return this._removeImageLayer(groupId, config);
                case 'raster-style-layer':
                    return this._removeRasterStyleLayer(groupId, config);

                default:
                    return true; // No-op for unknown types
            }
        } catch (error) {
            console.error(`Error removing layer group ${groupId}:`, error);
            return false;
        }
    }

    /**
     * Update layer opacity
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @param {number} opacity - Opacity value (0-1)
     * @returns {boolean} - Success status
     */
    updateLayerOpacity(groupId, config, opacity) {
        try {
            switch (config.type) {
                case 'vector':
                    return this._updateVectorLayerOpacity(groupId, config, opacity);
                case 'tms':
                    return this._updateTMSLayerOpacity(groupId, config, opacity);
                case 'wmts':
                    return this._updateWMTSLayerOpacity(groupId, config, opacity);
                case 'wms':
                    return this._updateWMSLayerOpacity(groupId, config, opacity);
                case 'cog':
                    return this._updateCOGLayerOpacity(groupId, config, opacity);
                case 'geojson':
                    return this._updateGeoJSONLayerOpacity(groupId, config, opacity);
                case 'js':
                    return this._updateGeoJSONLayerOpacity(groupId, config, opacity);
                case 'overpass':
                    return this._updateGeoJSONLayerOpacity(groupId, config, opacity);
                case 'img':
                    return this._updateImageLayerOpacity(groupId, config, opacity);
                case 'raster-style-layer':
                    return this._updateRasterStyleLayerOpacity(groupId, config, opacity);
                default:
                    return false;
            }
        } catch (error) {
            console.error(`Error updating layer opacity ${groupId}:`, error);
            return false;
        }
    }

    // Style layer methods
    _createStyleLayer(groupId, config, visible) {
        // Style layers are already in the map, just need to control visibility
        if (config.layers) {
            const styleLayers = this._map.getStyle().layers;
            let totalLayersProcessed = 0;

            config.layers.forEach(layer => {
                const layerIds = styleLayers
                    .filter(styleLayer => styleLayer['source-layer'] === layer.sourceLayer)
                    .map(styleLayer => styleLayer.id);

                if (layerIds.length === 0) {
                }

                layerIds.forEach(layerId => {
                    if (this._map.getLayer(layerId)) {
                        // When creating/showing a style layer, make sure visibility matches the expected state
                        this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                        totalLayersProcessed++;
                    }
                });
            });

            return true;
        }
        return false;
    }

    _updateStyleLayerVisibility(groupId, config, visible) {
        return this._createStyleLayer(groupId, config, visible);
    }

    _removeStyleLayer(groupId, config) {
        // Style layers are part of the base style, just hide them
        return this._updateStyleLayerVisibility(groupId, config, false);
    }

    // Vector layer methods
    async _createVectorLayer(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        }

        const sourceId = `vector-${groupId}`;

        if (!this._map.getSource(sourceId)) {
            // Add source
            const sourceConfig = {
                type: 'vector',
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22
            };

            if (config.url.startsWith('mapbox://')) {
                sourceConfig.url = config.url;
            } else {
                sourceConfig.tiles = [config.url];
            }

            if (config.inspect?.id) {
                sourceConfig.promoteId = { [config.sourceLayer]: config.inspect.id };
            }

            // Add attribution if available
            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            // Add layers based on style properties
            await this._addVectorLayers(groupId, config, sourceId, visible);
        } else {
            // Update visibility only
            this._updateVectorLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    async _addVectorLayers(groupId, config, sourceId, visible) {
        // Get default styles for checking what layer types should be created
        const defaultStyles = this._defaultStyles.vector || {};

        // Split the style object into one or more variants based on "prefix/prop" keys.
        // Each variant becomes its own set of map layers sharing the same source.
        const variants = this._parseStyleVariants(config.style);

        for (const variant of variants) {
            const variantStyle = variant.style;
            const variantSuffix = this._getVariantSuffix(variant.prefix);
            // Only the base variant gets defaults applied; secondary variants
            // are pure overlays driven by the properties the user supplied.
            const isBaseVariant = variant.prefix === '';

            // Check if user has explicitly defined any styles
            const userHasFillStyles = variantStyle['fill-color'] || variantStyle['fill-opacity'];
            const userHasLineStyles = variantStyle['line-color'] || variantStyle['line-width'];
            const userHasTextStyles = variantStyle['text-field'];
            const userHasCircleStyles = variantStyle['circle-radius'] || variantStyle['circle-color'];
            const userHasFillExtrusionStyles =
                variantStyle['fill-extrusion-height'] !== undefined ||
                variantStyle['fill-extrusion-color'] !== undefined ||
                variantStyle['fill-extrusion-opacity'] !== undefined;

            // If user has line styles but no fill, don't apply default fill (circle properties are decorative, not indicative of fill intent)
            const userOnlyHasLineStyles = userHasLineStyles && !userHasFillStyles;

            // Check if fill layer should be created
            // If user only has line styles or fill-extrusion styles, don't create fill layer even if defaults exist
            const hasFillStyles = !userHasFillExtrusionStyles && (userHasFillStyles ||
                (isBaseVariant && !userOnlyHasLineStyles && defaultStyles.fill && (defaultStyles.fill['fill-color'] || defaultStyles.fill['fill-opacity'])));

            // Check if line layer should be created (user styles or defaults)
            const hasLineStyles = userHasLineStyles ||
                (isBaseVariant && defaultStyles.line && (defaultStyles.line['line-color'] || defaultStyles.line['line-width']));

            // Check if text layer should be created (user styles or defaults)
            const hasTextStyles = userHasTextStyles ||
                (isBaseVariant && defaultStyles.text && defaultStyles.text['text-field']);

            // Check if circle layer should be created (only if user explicitly defines circle properties)
            const hasCircleStyles = userHasCircleStyles;

            // Add fill-extrusion layer if extrusion properties are defined
            if (userHasFillExtrusionStyles) {
                const extrusionStyle = this._filterStyleForLayerType(variantStyle, 'fill-extrusion');

                const layerConfig = this._createLayerConfig({
                    id: `vector-layer-${groupId}${variantSuffix}`,
                    groupId: groupId,
                    type: 'fill-extrusion',
                    source: sourceId,
                    'source-layer': config.sourceLayer || 'default',
                    style: extrusionStyle,
                    filter: config.filter,
                    visible,
                    applyDefaults: isBaseVariant
                }, 'fill-extrusion');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'fill', config, this._orderedGroups));
            }

            // Add fill layer
            if (hasFillStyles) {
                // Filter style to only include fill-related properties
                const fillStyle = this._filterStyleForLayerType(variantStyle, 'fill');

                const layerConfig = this._createLayerConfig({
                    id: `vector-layer-${groupId}${variantSuffix}`,
                    groupId: groupId,
                    type: 'fill',
                    source: sourceId,
                    'source-layer': config.sourceLayer || 'default',
                    style: fillStyle,
                    filter: config.filter,
                    visible,
                    applyDefaults: isBaseVariant
                }, 'fill');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'fill', config, this._orderedGroups));
            }

            // Add line layer
            if (hasLineStyles) {
                // Filter style to only include line-related properties
                const lineStyle = this._filterStyleForLayerType(variantStyle, 'line');

                // Use feature-state for line-offset so hover/selection effects are instant
                // (base variant only — secondary variants should not impose interaction offsets)
                if (isBaseVariant && !lineStyle['line-offset']) {
                    lineStyle['line-offset'] = ['case',
                        ['boolean', ['feature-state', 'selected'], false], -1,
                        ['boolean', ['feature-state', 'hover'], false], 1,
                        0
                    ];
                }

                const layerConfig = this._createLayerConfig({
                    id: `vector-layer-${groupId}-outline${variantSuffix}`,
                    groupId: groupId,
                    type: 'line',
                    source: sourceId,
                    'source-layer': config.sourceLayer || 'default',
                    style: lineStyle,
                    filter: config.filter,
                    visible,
                    applyDefaults: isBaseVariant
                }, 'line');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'line', config, this._orderedGroups));
            }

            // Add circle layer if circle properties are defined
            if (hasCircleStyles) {
                // Filter style to only include circle-related properties
                const circleStyle = this._filterStyleForLayerType(variantStyle, 'circle');

                const layerConfig = this._createLayerConfig({
                    id: `vector-layer-${groupId}-circle${variantSuffix}`,
                    groupId: groupId,
                    type: 'circle',
                    source: sourceId,
                    'source-layer': config.sourceLayer || 'default',
                    style: circleStyle,
                    filter: config.filter,
                    visible,
                    applyDefaults: isBaseVariant
                }, 'circle');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'circle', config, this._orderedGroups));
            }

            // Add text layer
            if (hasTextStyles) {
                // Filter style to only include symbol/text-related properties
                let symbolStyle = this._filterStyleForLayerType(variantStyle, 'symbol');

                // Prepare custom icon images if present
                symbolStyle = await this._prepareSymbolLayerIcons(symbolStyle);

                const layerConfig = this._createLayerConfig({
                    id: `vector-layer-${groupId}-text${variantSuffix}`,
                    groupId: groupId,
                    type: 'symbol',
                    source: sourceId,
                    'source-layer': config.sourceLayer || 'default',
                    style: symbolStyle,
                    filter: config.filter,
                    visible,
                    applyDefaults: isBaseVariant
                }, 'symbol');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'symbol', config, this._orderedGroups));
            }
        }
    }

    _updateVectorLayerVisibility(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        } else {
            this._stopBlinking(groupId, config);
        }

        this._getVariantPrefixes(config).forEach(prefix => {
            const suffix = this._getVariantSuffix(prefix);
            const layers = [
                `vector-layer-${groupId}${suffix}`,
                `vector-layer-${groupId}-outline${suffix}`,
                `vector-layer-${groupId}-circle${suffix}`,
                `vector-layer-${groupId}-text${suffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                }
            });
        });

        return true;
    }

    _removeVectorLayer(groupId, config) {
        this._stopBlinking(groupId, config);

        const sourceId = `vector-${groupId}`;

        this._getVariantPrefixes(config).forEach(prefix => {
            const suffix = this._getVariantSuffix(prefix);
            const layers = [
                `vector-layer-${groupId}${suffix}`,
                `vector-layer-${groupId}-outline${suffix}`,
                `vector-layer-${groupId}-circle${suffix}`,
                `vector-layer-${groupId}-text${suffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.removeLayer(layerId);
                }
            });
        });

        // Remove source
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }

        return true;
    }

    _updateVectorLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        this._getVariantPrefixes(config).forEach(prefix => {
            const suffix = this._getVariantSuffix(prefix);
            const fillId = `vector-layer-${groupId}${suffix}`;
            const outlineId = `vector-layer-${groupId}-outline${suffix}`;
            const circleId = `vector-layer-${groupId}-circle${suffix}`;
            const textId = `vector-layer-${groupId}-text${suffix}`;

            const fillLayer = this._map.getLayer(fillId);
            if (fillLayer) {
                const fillOpacityProp = fillLayer.type === 'fill-extrusion' ? 'fill-extrusion-opacity' : 'fill-opacity';
                this._map.setPaintProperty(fillId, fillOpacityProp, finalOpacity);
            }
            if (this._map.getLayer(outlineId)) {
                this._map.setPaintProperty(outlineId, 'line-opacity', finalOpacity);
            }
            if (this._map.getLayer(circleId)) {
                this._map.setPaintProperty(circleId, 'circle-opacity', finalOpacity);
                this._map.setPaintProperty(circleId, 'circle-stroke-opacity', finalOpacity);
            }
            if (this._map.getLayer(textId)) {
                this._map.setPaintProperty(textId, 'text-opacity', finalOpacity);
                this._map.setPaintProperty(textId, 'icon-opacity', finalOpacity);
            }
        });
        return true;
    }

    // TMS layer methods
    _createTMSLayer(groupId, config, visible) {
        const sourceId = `tms-${groupId}`;
        const layerId = `tms-layer-${groupId}`;

        if (!this._map.getSource(sourceId)) {
            const sourceConfig = {
                type: 'raster',
                tileSize: 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22
            };

            if (config.scheme) {
                sourceConfig.scheme = config.scheme;
            }

            if (config.url.startsWith('mapbox://')) {
                sourceConfig.url = config.url;
            } else {
                let tileUrl = config.url;

                // Wrap with proxy if configured
                if (config.proxyUrl) {
                    const encodedTileUrl = encodeURIComponent(tileUrl)
                        .replace(/%7Bz%7D/g, '{z}')
                        .replace(/%7Bx%7D/g, '{x}')
                        .replace(/%7By%7D/g, '{y}');
                    tileUrl = `${config.proxyUrl}?url=${encodedTileUrl}`;
                    if (config.proxyReferer) {
                        const encodedReferer = encodeURIComponent(config.proxyReferer);
                        tileUrl += `&referer=${encodedReferer}`;
                    }
                }

                sourceConfig.tiles = [tileUrl];
            }

            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'tms', null, config, this._orderedGroups));

            if (config.geojson && visible) {
                this._addSimpleStyleGeoJSONOverlay(groupId, config.geojson, visible);
            }
        } else {
            this._updateTMSLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    _updateTMSLayerVisibility(groupId, config, visible) {
        const layerId = `tms-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }

        if (config.geojson) {
            const geojsonSourceId = `tms-geojson-${groupId}`;
            if (visible && !this._map.getSource(geojsonSourceId)) {
                this._addSimpleStyleGeoJSONOverlay(groupId, config.geojson, visible);
            } else {
                this._updateSimpleStyleGeoJSONOverlayVisibility(groupId, visible);
            }
        }

        return true;
    }

    _removeTMSLayer(groupId, config) {
        const sourceId = `tms-${groupId}`;
        const layerId = `tms-layer-${groupId}`;

        if (this._map.getLayer(layerId)) {
            this._map.removeLayer(layerId);
        }
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }

        if (config.geojson) {
            this._removeSimpleStyleGeoJSONOverlay(groupId);
        }

        return true;
    }

    _updateTMSLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const layerId = `tms-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setPaintProperty(layerId, 'raster-opacity', finalOpacity);
        }
        return true;
    }

    // COG (Cloud Optimized GeoTIFF) layer methods
    // Uses Mapbox GL JS 3.23+ TileProvider API + a geotiff.js-backed provider
    // module (js/cog-tile-provider.js) that resolves tiles via HTTP range requests.
    _createCOGLayer(groupId, config, visible) {
        if (typeof mapboxgl === 'undefined' || typeof mapboxgl.addTileProvider !== 'function') {
            console.warn(`COG layer "${config.id}" requires Mapbox GL JS 3.23+ TileProvider API`);
            return false;
        }
        registerCOGProvider();

        const sourceId = `cog-${groupId}`;
        const layerId = `cog-layer-${groupId}`;

        if (!this._map.getSource(sourceId)) {
            const sourceConfig = {
                type: 'raster',
                provider: 'cog',
                url: config.url,
                tileSize: config.tileSize || 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22,
            };

            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'cog', null, config, this._orderedGroups));
        } else {
            this._updateCOGLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    _updateCOGLayerVisibility(groupId, config, visible) {
        const layerId = `cog-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
        return true;
    }

    _removeCOGLayer(groupId, config) {
        const sourceId = `cog-${groupId}`;
        const layerId = `cog-layer-${groupId}`;

        if (this._map.getLayer(layerId)) {
            this._map.removeLayer(layerId);
        }
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }
        return true;
    }

    _updateCOGLayerOpacity(groupId, config, opacity) {
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const layerId = `cog-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setPaintProperty(layerId, 'raster-opacity', finalOpacity);
        }
        return true;
    }

    _addSimpleStyleGeoJSONOverlay(groupId, geojson, visible) {
        const sourceId = `tms-geojson-${groupId}`;

        if (this._map.getSource(sourceId)) {
            return;
        }

        this._map.addSource(sourceId, {
            type: 'geojson',
            data: geojson
        });

        const fillLayerId = `${sourceId}-fill`;
        const lineLayerId = `${sourceId}-line`;

        const fillLayerConfig = this._createLayerConfig({
            id: fillLayerId,
            groupId: groupId,
            type: 'fill',
            source: sourceId,
            style: {
                'fill-color': ['coalesce', ['get', 'fill'], '#ff6b6b'],
                'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.1]
            },
            visible
        }, 'fill');

        const lineLayerConfig = this._createLayerConfig({
            id: lineLayerId,
            groupId: groupId,
            type: 'line',
            source: sourceId,
            style: {
                'line-color': ['coalesce', ['get', 'stroke'], '#ff6b6b'],
                'line-width': ['coalesce', ['get', 'stroke-width'], 2],
                'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 0.8]
            },
            visible
        }, 'line');

        this._addLayerWithSlot(fillLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'fill', {}, this._orderedGroups));
        this._addLayerWithSlot(lineLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'line', {}, this._orderedGroups));
    }

    _updateSimpleStyleGeoJSONOverlayVisibility(groupId, visible) {
        const sourceId = `tms-geojson-${groupId}`;
        const fillLayerId = `${sourceId}-fill`;
        const lineLayerId = `${sourceId}-line`;

        if (this._map.getLayer(fillLayerId)) {
            this._map.setLayoutProperty(fillLayerId, 'visibility', visible ? 'visible' : 'none');
        }
        if (this._map.getLayer(lineLayerId)) {
            this._map.setLayoutProperty(lineLayerId, 'visibility', visible ? 'visible' : 'none');
        }
    }

    _removeSimpleStyleGeoJSONOverlay(groupId) {
        const sourceId = `tms-geojson-${groupId}`;
        const fillLayerId = `${sourceId}-fill`;
        const lineLayerId = `${sourceId}-line`;

        if (this._map.getLayer(lineLayerId)) {
            this._map.removeLayer(lineLayerId);
        }
        if (this._map.getLayer(fillLayerId)) {
            this._map.removeLayer(fillLayerId);
        }
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }
    }

    // WMTS layer methods
    _createWMTSLayer(groupId, config, visible) {
        const sourceId = `wmts-${groupId}`;
        const layerId = `wmts-layer-${groupId}`;

        if (!this._map.getSource(sourceId)) {
            // Store current config for URL conversion
            this._currentConfig = config;

            // Convert WMTS URL to XYZ tile format for Mapbox GL JS
            const tileUrl = this._convertWMTSToXYZ(config.url);

            const sourceConfig = {
                type: 'raster',
                tileSize: config.tileSize || 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22
            };

            sourceConfig.tiles = [tileUrl];

            // Add attribution if available
            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'wmts', null, config, this._orderedGroups));

            // Add error handling for failed tile requests
            this._map.on('error', (e) => {
                if (e.sourceId === sourceId) {
                }
            });
        } else {
            this._updateWMTSLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    /**
     * Convert WMTS URL to XYZ tile URL format for Mapbox GL JS
     * @param {string} wmtsUrl - Original WMTS URL
     * @returns {string} - XYZ tile URL
     */
    _convertWMTSToXYZ(wmtsUrl) {
        let xyzUrl = wmtsUrl;

        // Replace WMTS tile matrix parameters with XYZ placeholders
        xyzUrl = xyzUrl.replace(/TileMatrix=\d+/gi, 'TileMatrix={z}');
        xyzUrl = xyzUrl.replace(/TileCol=\d+/gi, 'TileCol={x}');
        xyzUrl = xyzUrl.replace(/TileRow=\d+/gi, 'TileRow={y}');

        // NASA GIBS: Keep EPSG:4326 for better compatibility
        // GIBS doesn't natively store data in EPSG:3857, and many layers
        // are not available for on-the-fly reprojection to Web Mercator

        // For layers that support EPSG:3857, we can try conversion
        if (this._shouldConvertToWebMercator(wmtsUrl, this._currentConfig)) {
            // Convert EPSG:4326 to EPSG:3857 for Web Mercator projection
            xyzUrl = xyzUrl.replace(/epsg4326/gi, 'epsg3857');
            xyzUrl = xyzUrl.replace(/epsg:4326/gi, 'epsg:3857');

            // Update tilematrixset for Web Mercator projection
            if (xyzUrl.includes('tilematrixset=31.25m')) {
                xyzUrl = xyzUrl.replace(/tilematrixset=31\.25m/, 'tilematrixset=GoogleMapsCompatible_Level9');
            } else if (xyzUrl.includes('tilematrixset=15.625m')) {
                xyzUrl = xyzUrl.replace(/tilematrixset=15\.625m/, 'tilematrixset=GoogleMapsCompatible_Level9');
            } else if (!xyzUrl.includes('tilematrixset=GoogleMapsCompatible_Level')) {
                // Default to Level9 for unknown tilematrixsets in Web Mercator
                xyzUrl = xyzUrl.replace(/tilematrixset=[^&]+/, 'tilematrixset=GoogleMapsCompatible_Level9');
            }
        }

        return xyzUrl;
    }

    /**
     * Determine if a WMTS URL should be converted to Web Mercator (EPSG:3857)
     * @param {string} wmtsUrl - Original WMTS URL
     * @param {Object} config - Layer configuration that may override conversion
     * @returns {boolean} - True if conversion should be attempted
     */
    _shouldConvertToWebMercator(wmtsUrl, config = {}) {
        // Check if config explicitly forces projection conversion
        if (config.forceWebMercator === true) {
            return true;
        }
        if (config.forceWebMercator === false) {
            return false;
        }

        // Mapbox GL JS requires Web Mercator (EPSG:3857) tiles
        // So we must attempt conversion for all layers
        // Let individual layers fail gracefully if they don't support EPSG:3857
        return true;
    }

    _updateWMTSLayerVisibility(groupId, config, visible) {
        const layerId = `wmts-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
        return true;
    }

    _removeWMTSLayer(groupId, config) {
        const sourceId = `wmts-${groupId}`;
        const layerId = `wmts-layer-${groupId}`;

        if (this._map.getLayer(layerId)) {
            this._map.removeLayer(layerId);
        }
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }
        return true;
    }

    _updateWMTSLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const layerId = `wmts-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setPaintProperty(layerId, 'raster-opacity', finalOpacity);
        }
        return true;
    }

    // WMS layer methods
    _createWMSLayer(groupId, config, visible) {
        const sourceId = `wms-${groupId}`;
        const layerId = `wms-layer-${groupId}`;

        if (!this._map.getSource(sourceId)) {
            // Convert WMS URL to tile format for Mapbox GL JS
            const tileUrl = this._convertWMSToTiles(config.url, config.tileSize, config.srs, config);

            const sourceConfig = {
                type: 'raster',
                tileSize: config.tileSize || 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22
            };

            sourceConfig.tiles = [tileUrl];

            // Add attribution if available
            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'wms', null, config, this._orderedGroups));

            // Add error handling for failed tile requests
            this._map.on('error', (e) => {
                if (e.sourceId === sourceId) {
                }
            });
        } else {
            this._updateWMSLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    /**
     * Convert WMS URL to tile URL format for Mapbox GL JS
     * @param {string} wmsUrl - Original WMS URL
     * @param {number} tileSize - Tile size (default 256)
     * @returns {string} - Tile URL
     */
    _convertWMSToTiles(wmsUrl, tileSize = 256, srs = null, config = {}) {
        // Parse the URL to extract base URL and existing parameters
        const urlParts = wmsUrl.split('?');
        const baseUrl = urlParts[0];
        const searchParams = new URLSearchParams(urlParts[1] || '');

        // Extract parameters (case-insensitive)
        const params = {};
        for (const [key, value] of searchParams.entries()) {
            params[key.toLowerCase()] = value;
        }

        // Determine the SRS/CRS to use
        const targetSrs = srs || params.srs || params.crs || 'EPSG:3857';

        // Determine WMS version
        const version = params.version || '1.1.1';
        const useCRS = version >= '1.3.0';

        // Choose the appropriate bbox placeholder based on SRS
        const bboxPlaceholder = targetSrs === 'EPSG:4326' ? '{bbox-epsg-4326}' : '{bbox-epsg-3857}';

        // Build parameter object with required values
        // TileCache expects parameters in a specific order for cache key generation
        const orderedParams = {
            'styles': params.styles || '',
            'bbox': bboxPlaceholder,
            'format': params.format || 'image/png',
            'service': params.service || 'WMS',
            'version': version,
            'request': params.request || 'GetMap',
            [useCRS ? 'crs' : 'srs']: targetSrs,
            'transparent': params.transparent || 'true',
            'width': tileSize.toString(),
            'height': tileSize.toString(),
            'layers': params.layers || ''
        };

        // Reconstruct URL with ordered parameters
        const paramString = Object.entries(orderedParams)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        let tileUrl = `${baseUrl}?${paramString}`;

        // Wrap with proxy if configured
        if (config.proxyUrl) {
            const encodedWmsUrl = encodeURIComponent(tileUrl)
                .replace(/%7Bbbox-epsg-3857%7D/g, '{bbox-epsg-3857}')
                .replace(/%7Bbbox-epsg-4326%7D/g, '{bbox-epsg-4326}');
            const encodedReferer = config.proxyReferer ? encodeURIComponent(config.proxyReferer) : '';
            tileUrl = `${config.proxyUrl}?url=${encodedWmsUrl}`;
            if (config.proxyReferer) {
                tileUrl += `&referer=${encodedReferer}`;
            }
        }

        return tileUrl;
    }

    /**
     * Update WMS layer with new time-based URL
     */
    _updateWMSLayerTime(groupId, config, newUrl) {
        const sourceId = `wms-${groupId}`;
        const source = this._map.getSource(sourceId);

        if (source) {
            // Convert the new time-based URL to tile format
            const tileUrl = this._convertWMSToTiles(newUrl, config.tileSize, config.srs, config);

            // Remove and re-add source with new URL
            const layerId = `wms-layer-${groupId}`;
            if (this._map.getLayer(layerId)) {
                this._map.removeLayer(layerId);
            }
            this._map.removeSource(sourceId);

            // Add source with new URL
            const sourceConfig = {
                type: 'raster',
                tileSize: config.tileSize || 256,
                minzoom: config.minzoom || 0,
                maxzoom: config.maxzoom || 22,
                tiles: [tileUrl]
            };

            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);

            // Re-add layer
            const layerConfig = this._createLayerConfig({
                id: layerId,
                groupId: groupId,
                source: sourceId,
                style: {
                    ...(this._defaultStyles.raster || {}),
                    ...(config.style || {}),
                    'raster-opacity': config.style?.['raster-opacity'] || config.opacity || this._defaultStyles.raster?.['raster-opacity'] || 1
                },
                visible: true
            }, 'raster');

            this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'wms', null, config, this._orderedGroups));
        }
    }

    _updateWMSLayerVisibility(groupId, config, visible) {
        const layerId = `wms-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
        return true;
    }

    _removeWMSLayer(groupId, config) {
        const sourceId = `wms-${groupId}`;
        const layerId = `wms-layer-${groupId}`;

        if (this._map.getLayer(layerId)) {
            this._map.removeLayer(layerId);
        }
        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }
        return true;
    }

    _updateWMSLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const layerId = `wms-layer-${groupId}`;
        if (this._map.getLayer(layerId)) {
            this._map.setPaintProperty(layerId, 'raster-opacity', finalOpacity);
        }
        return true;
    }

    // GeoJSON layer methods
    async _createGeoJSONLayer(groupId, config, visible) {
        const sourceId = `geojson-${groupId}`;

        // If explicitly requested to cluster by attribute, handle it separately
        if (config.clusterSeparateBy) {
            return this._createSegregatedGeoJSONLayer(groupId, config, visible);
        }

        if (!this._map.getSource(sourceId) && visible) {
            let dataSource;

            if (config.data) {
                dataSource = this._processGeoJSONData(config.data);
            } else if (config.geojson !== undefined) {
                // Support geojson property (can be null or a GeoJSON object)
                dataSource = config.geojson ? this._processGeoJSONData(config.geojson) : { type: 'FeatureCollection', features: [] };
            } else if (config.dataSource === 'localStorage') {
                // Retrieve GeoJSON from localStorage cache; fall back to URL if absent
                const storedData = LayerConfigGenerator.retrieveGeoJSONData(config.id);
                if (storedData) {
                    dataSource = this._processGeoJSONData(storedData);
                } else if (config.url) {
                    if (KMLConverter.isKmlUrl(config.url)) {
                        try {
                            dataSource = await KMLConverter.fetchAndConvert(config.url);
                        } catch (error) {
                            console.error(`Error converting KML for ${groupId}:`, error);
                            return false;
                        }
                    } else {
                        dataSource = config.url;
                    }
                } else {
                    console.error(`GeoJSON data not found in localStorage and no URL fallback for ${groupId}`);
                    return false;
                }
            } else if (config.url) {
                if (KMLConverter.isKmlUrl(config.url)) {
                    try {
                        dataSource = await KMLConverter.fetchAndConvert(config.url);
                    } catch (error) {
                        console.error(`Error converting KML for ${groupId}:`, error);
                        return false;
                    }
                } else {
                    dataSource = config.url;
                }
            } else {
                console.error('GeoJSON layer missing data, geojson, and URL:', groupId);
                return false;
            }

            const sourceConfig = {
                type: 'geojson',
                data: dataSource
            };

            // Add clustering config if enabled
            if (config.clustered) {
                sourceConfig.cluster = true;
                sourceConfig.clusterMaxZoom = config.clusterMaxZoom || 14;
                sourceConfig.clusterRadius = config.clusterRadius || 50;
            }

            if (config.inspect?.id) {
                sourceConfig.promoteId = config.inspect.id;
            }

            // Add attribution if available
            if (config.attribution) {
                sourceConfig.attribution = config.attribution;
            }

            this._map.addSource(sourceId, sourceConfig);
            await this._addGeoJSONLayers(groupId, config, sourceId, visible);

            if (config.refresh && config.url) {
                this._setupGeoJSONRefresh(groupId, config);
            }
        } else {
            this._updateGeoJSONLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    _setupGeoJSONRefresh(groupId, config) {
        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
        }

        const timer = setInterval(async () => {
            const sourceId = `geojson-${groupId}`;
            if (!this._map.getSource(sourceId)) {
                clearInterval(timer);
                this._refreshTimers.delete(groupId);
                return;
            }

            try {
                const response = await fetch(config.url);
                const data = await response.json();
                const geojson = this._processGeoJSONData(data);
                this._map.getSource(sourceId).setData(geojson);
            } catch (error) {
                console.error(`Error refreshing GeoJSON layer ${groupId}:`, error);
            }
        }, config.refresh);

        this._refreshTimers.set(groupId, timer);
    }

    // JS layer methods
    // A `js` layer fetches data from `config.url` and hands it to an
    // atlas-defined function (config/{atlas}.js → `dataFunctions[id]`) that
    // gathers/paginates/transforms the response into GeoJSON. The resulting
    // GeoJSON is then rendered through the same pipeline as a `geojson` layer.
    async _createJsLayer(groupId, config, visible) {
        const sourceId = `geojson-${groupId}`;

        if (!this._map.getSource(sourceId) && visible) {
            let geojson;
            try {
                geojson = await this._fetchJsLayerData(config);
            } catch (error) {
                console.error(`Error loading data for js layer ${groupId}:`, error);
                return false;
            }

            // Strip `url`/`refresh` so `_createGeoJSONLayer` doesn't also try to
            // treat config.url as a raw GeoJSON endpoint and set up its own timer;
            // this layer's own refresh (re-running the atlas dataFunction) is
            // scheduled separately below.
            const created = await this._createGeoJSONLayer(groupId, { ...config, type: 'geojson', data: geojson, url: undefined, refresh: undefined }, visible);

            if (created && config.refresh) {
                this._setupJsLayerRefresh(groupId, config);
            }

            return created;
        }

        return this._updateGeoJSONLayerVisibility(groupId, config, visible);
    }

    async _fetchJsLayerData(config) {
        const atlasName = config._sourceAtlas;
        // Prefer the unprefixed original id — layer-registry.js prefixes ids
        // like `goa-aqi` when a layer is pulled cross-atlas, but the atlas's
        // own config/{atlas}.js dataFunctions are keyed by the original id.
        const functionName = config.dataFunction || config._originalId || config.id;

        if (!atlasName || !functionName) {
            throw new Error(`js layer "${config.id}" is missing atlas context or an id/dataFunction to resolve its data function`);
        }

        const dataFunctions = await handlerLoader.loadDataFunctions(atlasName);
        const fn = dataFunctions[functionName];

        if (typeof fn !== 'function') {
            throw new Error(`No dataFunction "${functionName}" exported from config/${atlasName}.js`);
        }

        const result = await fn({ url: config.url, config, layerId: config.id });
        return this._processGeoJSONData(result);
    }

    _setupJsLayerRefresh(groupId, config) {
        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
        }

        const timer = setInterval(async () => {
            const sourceId = `geojson-${groupId}`;
            if (!this._map.getSource(sourceId)) {
                clearInterval(timer);
                this._refreshTimers.delete(groupId);
                return;
            }

            try {
                const geojson = await this._fetchJsLayerData(config);
                this._map.getSource(sourceId).setData(geojson);
            } catch (error) {
                console.error(`Error refreshing js layer ${groupId}:`, error);
            }
        }, config.refresh);

        this._refreshTimers.set(groupId, timer);
    }

    async _createSegregatedGeoJSONLayer(groupId, config, visible) {
        // Fetch data first to process it
        let geojson;
        if (config.data) {
            geojson = this._processGeoJSONData(config.data);
        } else if (config.geojson !== undefined) {
            geojson = config.geojson ? this._processGeoJSONData(config.geojson) : { type: 'FeatureCollection', features: [] };
        } else if (config.dataSource === 'localStorage') {
            const storedData = LayerConfigGenerator.retrieveGeoJSONData(config.id);
            if (storedData) {
                geojson = this._processGeoJSONData(storedData);
            } else if (config.url) {
                try {
                    if (KMLConverter.isKmlUrl(config.url)) {
                        geojson = await KMLConverter.fetchAndConvert(config.url);
                    } else {
                        const response = await fetch(config.url);
                        const data = await response.json();
                        geojson = this._processGeoJSONData(data);
                    }
                } catch (error) {
                    console.error(`Error loading data for segregated layer ${groupId}:`, error);
                    return false;
                }
            } else {
                console.error(`GeoJSON data not found in localStorage and no URL fallback for ${groupId}`);
                return false;
            }
        } else if (config.url) {
            try {
                if (KMLConverter.isKmlUrl(config.url)) {
                    geojson = await KMLConverter.fetchAndConvert(config.url);
                } else {
                    const response = await fetch(config.url);
                    const data = await response.json();
                    geojson = this._processGeoJSONData(data);
                }
            } catch (error) {
                console.error(`Error loading data for segregated layer ${groupId}:`, error);
                return false;
            }
        } else {
            return false;
        }

        // Store the sub-source IDs for management
        if (!this._layerCache.has(groupId)) {
            this._layerCache.set(groupId, { subSources: [] });
        }
        const cache = this._layerCache.get(groupId);

        // Group features by attribute
        const groups = {};
        geojson.features.forEach(feature => {
            const value = feature.properties[config.clusterSeparateBy] || 'other';
            // Sanitize value for ID
            const safeValue = String(value).replace(/[^a-zA-Z0-9-]/g, '_');

            if (!groups[safeValue]) {
                groups[safeValue] = {
                    features: [],
                    originalValue: value
                };
            }
            groups[safeValue].features.push(feature);
        });

        // Create a source and layers for each group
        for (const [safeValue, groupData] of Object.entries(groups)) {
            const subSourceId = `geojson-${groupId}-${safeValue}`;

            if (!this._map.getSource(subSourceId) && visible) {
                cache.subSources.push(subSourceId);

                const sourceConfig = {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: groupData.features
                    },
                    cluster: true,
                    clusterMaxZoom: config.clusterMaxZoom || 14,
                    clusterRadius: config.clusterRadius || 50
                };

                if (config.inspect?.id) {
                    sourceConfig.promoteId = config.inspect.id;
                }

                if (config.attribution) {
                    sourceConfig.attribution = config.attribution;
                }

                this._map.addSource(subSourceId, sourceConfig);

                // Add layers for this sub-source
                // Pass specific cluster color if defined in a map, otherwise generate one or use default
                const subConfig = { ...config, clustered: true };

                // If clusterStyles map is provided, check for specific style
                if (config.clusterStyles && config.clusterStyles[groupData.originalValue]) {
                    subConfig.clusterColor = config.clusterStyles[groupData.originalValue].color;
                }
                // Don't auto-generate colors, let _addGeoJSONLayers use default step unless overridden

                await this._addGeoJSONLayers(groupId, subConfig, subSourceId, visible, safeValue);
            }
        }

        return true;
    }

    _processGeoJSONData(data) {
        if (data.type === 'FeatureCollection') {
            return data;
        } else if (data.type === 'Feature') {
            return { type: 'FeatureCollection', features: [data] };
        } else if (data.type && data.coordinates) {
            return {
                type: 'FeatureCollection',
                features: [{ type: 'Feature', geometry: data, properties: {} }]
            };
        }
        throw new Error('Invalid GeoJSON data format');
    }

    async _addGeoJSONLayers(groupId, config, sourceId, visible, suffix = '') {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        }

        const segregateSuffix = suffix ? `-${suffix}` : '';
        // Get default styles for checking what layer types should be created
        const defaultStyles = this._defaultStyles.vector || {};

        // Common filter for non-clustered points if clustering is enabled
        const unclusteredFilter = config.clustered ? ['!', ['has', 'point_count']] : null;

        // Split the style into variants based on "prefix/prop" keys so each prefix
        // becomes its own map layer set sharing this source.
        const variants = this._parseStyleVariants(config.style);

        for (const variant of variants) {
            const variantStyle = variant.style;
            const variantSuffix = this._getVariantSuffix(variant.prefix);
            const idSuffix = `${segregateSuffix}${variantSuffix}`;
            const isBaseVariant = variant.prefix === '';

            // Check if user has explicitly defined any styles
            const userHasFillStyles = variantStyle['fill-color'] || variantStyle['fill-opacity'];
            const userHasLineStyles = variantStyle['line-color'] || variantStyle['line-width'];
            const userHasTextStyles = variantStyle['text-field'];
            const userHasCircleStyles = variantStyle['circle-radius'] || variantStyle['circle-color'];
            const userHasIconStyles = variantStyle['icon-image'];
            const userHasFillExtrusionStyles =
                variantStyle['fill-extrusion-height'] !== undefined ||
                variantStyle['fill-extrusion-color'] !== undefined ||
                variantStyle['fill-extrusion-opacity'] !== undefined;

            // If user has line styles but no fill, don't apply default fill (circle/icon properties are decorative, not indicative of fill intent)
            const userOnlyHasLineStyles = userHasLineStyles && !userHasFillStyles;

            // Check if fill layer should be created (user styles or defaults — defaults only for base variant)
            // If user only has line styles or fill-extrusion styles, don't create fill layer even if defaults exist
            const hasFillStyles = !userHasFillExtrusionStyles && (userHasFillStyles ||
                (isBaseVariant && !userOnlyHasLineStyles && defaultStyles.fill && (defaultStyles.fill['fill-color'] || defaultStyles.fill['fill-opacity'])));

            // Check if line layer should be created (user styles or defaults)
            const hasLineStyles = userHasLineStyles ||
                (isBaseVariant && defaultStyles.line && (defaultStyles.line['line-color'] || defaultStyles.line['line-width']));

            // Check if text layer should be created (user styles or defaults)
            const hasTextStyles = userHasTextStyles ||
                (isBaseVariant && defaultStyles.text && defaultStyles.text['text-field']);

            // Check if circle layer should be created (only if user explicitly defines circle properties)
            const hasCircleStyles = userHasCircleStyles;

            // Add fill-extrusion layer if extrusion properties are defined
            if (userHasFillExtrusionStyles) {
                const extrusionStyle = this._filterStyleForLayerType(variantStyle, 'fill-extrusion');

                const extrusionLayerConfig = this._createLayerConfig({
                    id: `${sourceId}-fill${idSuffix}`,
                    groupId: groupId,
                    type: 'fill-extrusion',
                    source: sourceId,
                    style: extrusionStyle,
                    visible,
                    applyDefaults: isBaseVariant,
                    ...(unclusteredFilter && { filter: unclusteredFilter })
                }, 'fill-extrusion');

                this._addLayerWithSlot(extrusionLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'fill', config, this._orderedGroups));
            }

            // Add fill layer
            if (hasFillStyles) {
                // Filter style to only include fill-related properties
                const fillStyle = this._filterStyleForLayerType(variantStyle, 'fill');

                const fillLayerConfig = this._createLayerConfig({
                    id: `${sourceId}-fill${idSuffix}`,
                    groupId: groupId,
                    type: 'fill',
                    source: sourceId,
                    style: fillStyle,
                    visible,
                    applyDefaults: isBaseVariant,
                    ...(unclusteredFilter && { filter: unclusteredFilter })
                }, 'fill');

                this._addLayerWithSlot(fillLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'fill', config, this._orderedGroups));
            }

            // Add line layer
            if (hasLineStyles) {
                // Filter style to only include line-related properties
                const lineStyle = this._filterStyleForLayerType(variantStyle, 'line');

                const lineLayerConfig = this._createLayerConfig({
                    id: `${sourceId}-line${idSuffix}`,
                    groupId: groupId,
                    type: 'line',
                    source: sourceId,
                    style: lineStyle,
                    visible,
                    applyDefaults: isBaseVariant,
                    ...(unclusteredFilter && { filter: unclusteredFilter })
                }, 'line');

                this._addLayerWithSlot(lineLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'line', config, this._orderedGroups));
            }

            // Add circle layer if circle properties are defined
            if (hasCircleStyles) {
                // Filter style to only include circle-related properties
                const circleStyle = this._filterStyleForLayerType(variantStyle, 'circle');

                const circleLayerConfig = this._createLayerConfig({
                    id: `${sourceId}-circle${idSuffix}`,
                    groupId: groupId,
                    type: 'circle',
                    source: sourceId,
                    style: circleStyle,
                    visible,
                    applyDefaults: isBaseVariant,
                    ...(unclusteredFilter && { filter: unclusteredFilter })
                }, 'circle');

                this._addLayerWithSlot(circleLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'circle', config, this._orderedGroups));
            }

            // Add text or icon layer if symbol properties are defined
            if (hasTextStyles || userHasIconStyles) {
                // Filter style to only include symbol/text-related properties
                let symbolStyle = this._filterStyleForLayerType(variantStyle, 'symbol');

                // Prepare custom icon images if present (handles both simple strings and expressions)
                symbolStyle = await this._prepareSymbolLayerIcons(symbolStyle);

                const symbolLayerConfig = this._createLayerConfig({
                    id: `${sourceId}-symbol${idSuffix}`,
                    groupId: groupId,
                    type: 'symbol',
                    source: sourceId,
                    style: symbolStyle,
                    visible,
                    applyDefaults: isBaseVariant,
                    ...(unclusteredFilter && { filter: unclusteredFilter })
                }, 'symbol');

                this._addLayerWithSlot(symbolLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'symbol', config, this._orderedGroups));
            }
        }

        // Restore idSuffix used below (for cluster layers) to the segregate suffix only —
        // cluster layers are not per-variant.
        const idSuffix = segregateSuffix;

        // Add cluster layers if enabled
        if (config.clustered) {
            // Cluster circles
            const clusterLayerConfig = this._createLayerConfig({
                id: `${sourceId}-clusters${idSuffix}`,
                groupId: groupId,
                type: 'circle',
                source: sourceId,
                filter: ['has', 'point_count'],
                style: {
                    'circle-color': config.clusterColor || [
                        'step',
                        ['get', 'point_count'],
                        '#51bbd6',
                        100,
                        '#f1f075',
                        750,
                        '#f28cb1'
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        100,
                        30,
                        750,
                        40
                    ]
                },
                visible
            }, 'circle');

            this._addLayerWithSlot(clusterLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'circle', config, this._orderedGroups));

            // Cluster counts
            const clusterCountLayerConfig = this._createLayerConfig({
                id: `${sourceId}-cluster-count${idSuffix}`,
                groupId: groupId,
                type: 'symbol',
                source: sourceId,
                filter: ['has', 'point_count'],
                style: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                },
                visible
            }, 'symbol');

            this._addLayerWithSlot(clusterCountLayerConfig, LayerOrderManager.getInsertPosition(this._map, 'vector', 'symbol', config, this._orderedGroups));
        }
    }

    _updateGeoJSONLayerVisibility(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        } else {
            this._stopBlinking(groupId, config);
        }

        const variantPrefixes = this._getVariantPrefixes(config);

        if (config.clusterSeparateBy) {
            const cache = this._layerCache.get(groupId);
            if (cache && cache.subSources) {
                cache.subSources.forEach(sourceId => {
                    const segregateSuffix = sourceId.replace(`geojson-${groupId}-`, '');
                    variantPrefixes.forEach(prefix => {
                        const variantSuffix = this._getVariantSuffix(prefix);
                        const idSuffix = `-${segregateSuffix}${variantSuffix}`;
                        const layers = [
                            `${sourceId}-fill${idSuffix}`,
                            `${sourceId}-line${idSuffix}`,
                            `${sourceId}-label${idSuffix}`,
                            `${sourceId}-symbol${idSuffix}`,
                            `${sourceId}-circle${idSuffix}`,
                            `${sourceId}-clusters${idSuffix}`,
                            `${sourceId}-cluster-count${idSuffix}`
                        ];
                        layers.forEach(layerId => {
                            if (this._map.getLayer(layerId)) {
                                this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                            }
                        });
                    });
                });
            }
            return true;
        }

        const sourceId = `geojson-${groupId}`;
        variantPrefixes.forEach(prefix => {
            const variantSuffix = this._getVariantSuffix(prefix);
            const layers = [
                `${sourceId}-fill${variantSuffix}`,
                `${sourceId}-line${variantSuffix}`,
                `${sourceId}-label${variantSuffix}`,
                `${sourceId}-symbol${variantSuffix}`,
                `${sourceId}-circle${variantSuffix}`,
                `${sourceId}-clusters${variantSuffix}`,
                `${sourceId}-cluster-count${variantSuffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                }
            });
        });

        if (visible && config.refresh && config.url && !this._refreshTimers.has(groupId)) {
            if (config.type === 'js') {
                this._setupJsLayerRefresh(groupId, config);
            } else {
                this._setupGeoJSONRefresh(groupId, config);
            }
        } else if (!visible && this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
            this._refreshTimers.delete(groupId);
        }

        return true;
    }

    _removeGeoJSONLayer(groupId, config) {
        this._stopBlinking(groupId, config);

        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
            this._refreshTimers.delete(groupId);
        }

        const variantPrefixes = this._getVariantPrefixes(config);

        if (config.clusterSeparateBy) {
            const cache = this._layerCache.get(groupId);
            if (cache && cache.subSources) {
                cache.subSources.forEach(sourceId => {
                    const segregateSuffix = sourceId.replace(`geojson-${groupId}-`, '');
                    variantPrefixes.forEach(prefix => {
                        const variantSuffix = this._getVariantSuffix(prefix);
                        const idSuffix = `-${segregateSuffix}${variantSuffix}`;
                        const layers = [
                            `${sourceId}-fill${idSuffix}`,
                            `${sourceId}-line${idSuffix}`,
                            `${sourceId}-label${idSuffix}`,
                            `${sourceId}-symbol${idSuffix}`,
                            `${sourceId}-circle${idSuffix}`,
                            `${sourceId}-clusters${idSuffix}`,
                            `${sourceId}-cluster-count${idSuffix}`
                        ];
                        layers.forEach(layerId => {
                            if (this._map.getLayer(layerId)) {
                                this._map.removeLayer(layerId);
                            }
                        });
                    });
                    if (this._map.getSource(sourceId)) {
                        this._map.removeSource(sourceId);
                    }
                });

                // Clear cache for this group
                cache.subSources = [];
            }
            return true;
        }

        const sourceId = `geojson-${groupId}`;
        variantPrefixes.forEach(prefix => {
            const variantSuffix = this._getVariantSuffix(prefix);
            const layers = [
                `${sourceId}-fill${variantSuffix}`,
                `${sourceId}-line${variantSuffix}`,
                `${sourceId}-label${variantSuffix}`,
                `${sourceId}-symbol${variantSuffix}`,
                `${sourceId}-circle${variantSuffix}`,
                `${sourceId}-clusters${variantSuffix}`,
                `${sourceId}-cluster-count${variantSuffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.removeLayer(layerId);
                }
            });
        });

        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }

        return true;
    }

    _updateGeoJSONLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const variantPrefixes = this._getVariantPrefixes(config);
        const setOp = (layer, prop, val) => {
            if (this._map.getLayer(layer)) this._map.setPaintProperty(layer, prop, val);
        };

        if (config.clusterSeparateBy) {
            const cache = this._layerCache.get(groupId);
            if (cache && cache.subSources) {
                cache.subSources.forEach(sourceId => {
                    const segregateSuffix = sourceId.replace(`geojson-${groupId}-`, '');
                    variantPrefixes.forEach(prefix => {
                        const variantSuffix = this._getVariantSuffix(prefix);
                        const idSuffix = `-${segregateSuffix}${variantSuffix}`;
                        setOp(`${sourceId}-fill${idSuffix}`, 'fill-opacity', finalOpacity * 0.5);
                        setOp(`${sourceId}-line${idSuffix}`, 'line-opacity', finalOpacity);
                        setOp(`${sourceId}-label${idSuffix}`, 'text-opacity', finalOpacity);
                        setOp(`${sourceId}-symbol${idSuffix}`, 'icon-opacity', finalOpacity);
                        setOp(`${sourceId}-symbol${idSuffix}`, 'text-opacity', finalOpacity);
                        setOp(`${sourceId}-circle${idSuffix}`, 'circle-opacity', finalOpacity);
                        setOp(`${sourceId}-clusters${idSuffix}`, 'circle-opacity', finalOpacity);
                        setOp(`${sourceId}-cluster-count${idSuffix}`, 'text-opacity', finalOpacity);
                    });
                });
            }
            return true;
        }

        const sourceId = `geojson-${groupId}`;
        variantPrefixes.forEach(prefix => {
            const variantSuffix = this._getVariantSuffix(prefix);
            const fillId = `${sourceId}-fill${variantSuffix}`;
            const lineId = `${sourceId}-line${variantSuffix}`;
            const labelId = `${sourceId}-label${variantSuffix}`;
            const symbolId = `${sourceId}-symbol${variantSuffix}`;
            const circleId = `${sourceId}-circle${variantSuffix}`;
            const clustersId = `${sourceId}-clusters${variantSuffix}`;
            const clusterCountId = `${sourceId}-cluster-count${variantSuffix}`;

            const fillLayer = this._map.getLayer(fillId);
            if (fillLayer) {
                if (fillLayer.type === 'fill-extrusion') {
                    this._map.setPaintProperty(fillId, 'fill-extrusion-opacity', finalOpacity);
                } else {
                    this._map.setPaintProperty(fillId, 'fill-opacity', finalOpacity * 0.5);
                }
            }
            setOp(lineId, 'line-opacity', finalOpacity);
            setOp(labelId, 'text-opacity', finalOpacity);
            if (this._map.getLayer(symbolId)) {
                this._map.setPaintProperty(symbolId, 'icon-opacity', finalOpacity);
                this._map.setPaintProperty(symbolId, 'text-opacity', finalOpacity);
            }
            setOp(circleId, 'circle-opacity', finalOpacity);
            setOp(clustersId, 'circle-opacity', finalOpacity);
            setOp(clusterCountId, 'text-opacity', finalOpacity);
        });

        return true;
    }

    // Overpass layer methods — delegate rendering to the GeoJSON pipeline,
    // own only the OverpassLoader lifecycle.
    async _createOverpassLayer(groupId, config, visible) {
        const sourceId = `geojson-${groupId}`;

        if (!this._map.getSource(sourceId) && visible) {
            this._map.addSource(sourceId, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
                ...(config.attribution ? { attribution: config.attribution } : {})
            });
            await this._addGeoJSONLayers(groupId, config, sourceId, visible);
        }

        this._startOverpassLoader(groupId, config, visible);
        return true;
    }

    _updateOverpassLayerVisibility(groupId, config, visible) {
        const sourceId = `geojson-${groupId}`;

        if (visible && !this._map.getSource(sourceId)) {
            return this._createOverpassLayer(groupId, config, visible);
        }

        this._updateGeoJSONLayerVisibility(groupId, config, visible);
        this._startOverpassLoader(groupId, config, visible);
        return true;
    }

    _removeOverpassLayer(groupId, config) {
        const loader = this._overpassLoaders.get(groupId);
        if (loader) {
            loader.destroy();
            this._overpassLoaders.delete(groupId);
        }
        return this._removeGeoJSONLayer(groupId, config);
    }

    _startOverpassLoader(groupId, config, visible) {
        let loader = this._overpassLoaders.get(groupId);

        if (!visible) {
            if (loader) loader.stop();
            return;
        }

        if (!loader) {
            const sourceId = `geojson-${groupId}`;
            loader = new OverpassLoader({
                map: this._map,
                groupId,
                config,
                onData: (geojson) => {
                    const source = this._map.getSource(sourceId);
                    if (source) source.setData(geojson);
                }
            });
            this._overpassLoaders.set(groupId, loader);
        }
        loader.start();
    }

    /**
     * Update GeoJSON layer data dynamically
     * @param {string} groupId - Layer group identifier
     * @param {Object} geojsonData - New GeoJSON data
     * @returns {boolean} - Success status
     */
    updateGeoJSONLayerData(groupId, geojsonData) {
        const sourceId = `geojson-${groupId}`;

        const source = this._map.getSource(sourceId);

        if (!source) {
            console.warn(`[MapboxAPI] Source ${sourceId} not found. Available sources:`, Object.keys(this._map.getStyle().sources));
            return false;
        }

        const processedData = geojsonData ? this._processGeoJSONData(geojsonData) : { type: 'FeatureCollection', features: [] };

        source.setData(processedData);

        return true;
    }

    async refreshLayerNow(groupId, config) {
        if (!config?.url) return false;
        try {
            if (config.type === 'geojson') {
                const sourceId = `geojson-${groupId}`;
                if (!this._map.getSource(sourceId)) return false;
                const response = await fetch(config.url);
                const data = await response.json();
                this._map.getSource(sourceId).setData(this._processGeoJSONData(data));
            } else if (config.type === 'js') {
                const sourceId = `geojson-${groupId}`;
                if (!this._map.getSource(sourceId)) return false;
                const geojson = await this._fetchJsLayerData(config);
                this._map.getSource(sourceId).setData(geojson);
            } else if (config.type === 'csv') {
                const sourceId = `csv-${groupId}`;
                if (!this._map.getSource(sourceId)) return false;
                const response = await fetch(config.url);
                const csvText = await response.text();
                this._map.getSource(sourceId).setData(this._processCSVData(csvText, config.csvParser));
            } else if (config.type === 'sheet') {
                const sourceId = `csv-${groupId}`;
                if (!this._map.getSource(sourceId)) return false;
                const geojson = await this._fetchSheetGeoJSON(config);
                this._map.getSource(sourceId).setData(geojson);
            }
            return true;
        } catch (error) {
            console.error(`Error refreshing layer ${groupId}:`, error);
            return false;
        }
    }

    // CSV layer methods
    async _createCSVLayer(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        }

        const sourceId = `csv-${groupId}`;

        if (!this._map.getSource(sourceId) && visible) {
            try {
                let geojson;

                if (config.data) {
                    geojson = this._processCSVData(config.data, config.csvParser);
                } else if (config.dataSource === 'localStorage') {
                    const storedData = LayerConfigGenerator.retrieveGeoJSONData(config.id);
                    if (storedData) {
                        geojson = this._processGeoJSONData(storedData);
                    } else if (config.url) {
                        const response = await fetch(config.url);
                        const csvText = await response.text();
                        geojson = this._processCSVData(csvText, config.csvParser);
                    } else {
                        console.error('CSV layer cache miss and no URL fallback:', groupId);
                        return false;
                    }
                } else if (config.url) {
                    const response = await fetch(config.url);
                    const csvText = await response.text();
                    geojson = this._processCSVData(csvText, config.csvParser);
                } else {
                    console.error('CSV layer missing both data and URL:', groupId);
                    return false;
                }

                const sourceConfig = {
                    type: 'geojson',
                    data: geojson
                };

                if (config.inspect?.id) {
                    sourceConfig.promoteId = config.inspect.id;
                }

                if (config.attribution) {
                    sourceConfig.attribution = config.attribution;
                }

                this._map.addSource(sourceId, sourceConfig);

                // Use the same layer creation logic as GeoJSON to support all layer types
                await this._addGeoJSONLayers(groupId, config, sourceId, visible);

                // Set up refresh if specified
                if (config.refresh && config.url) {
                    this._setupCSVRefresh(groupId, config);
                }
            } catch (error) {
                console.error(`Error loading CSV layer '${groupId}':`, error);
                return false;
            }
        } else {
            this._updateCSVLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    _processCSVData(data, csvParser) {
        let rows;
        if (Array.isArray(data)) {
            rows = data;
        } else if (typeof data === 'string') {
            rows = csvParser ? csvParser(data) : DataUtils.parseCSV(data);
        } else {
            throw new Error('Invalid CSV data format');
        }
        return GeoUtils.rowsToGeoJSON(rows);
    }

    // Sheet layer methods — a "sheet" layer is a `csv` layer whose data comes
    // from every tab of a Google Sheet merged together, instead of a single
    // tab's export URL. It reuses the exact same source/layer plumbing as
    // `csv` (`csv-${groupId}`) — only how the GeoJSON is fetched differs.
    async _createSheetLayer(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        }

        const sourceId = `csv-${groupId}`;

        if (!this._map.getSource(sourceId) && visible) {
            try {
                if (!config.url) {
                    console.error('Sheet layer missing URL:', groupId);
                    return false;
                }

                const geojson = await this._fetchSheetGeoJSON(config);

                const sourceConfig = {
                    type: 'geojson',
                    data: geojson
                };

                if (config.inspect?.id) {
                    sourceConfig.promoteId = config.inspect.id;
                }

                if (config.attribution) {
                    sourceConfig.attribution = config.attribution;
                }

                this._map.addSource(sourceId, sourceConfig);

                // Use the same layer creation logic as GeoJSON/CSV to support all layer types
                await this._addGeoJSONLayers(groupId, config, sourceId, visible);

                if (config.refresh) {
                    this._setupSheetRefresh(groupId, config);
                }
            } catch (error) {
                console.error(`Error loading sheet layer '${groupId}':`, error);
                return false;
            }
        } else {
            this._updateCSVLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    // Fetches every tab of config.url's spreadsheet and merges them into one
    // GeoJSON FeatureCollection (see js/google-sheets-api.js — Google's CSV
    // export only ever returns a single tab, so this is N fetches, not one).
    async _fetchSheetGeoJSON(config) {
        const rows = await GoogleSheetsAPI.fetchAllRowsFromUrl(config.url);
        return GeoUtils.rowsToGeoJSON(rows);
    }

    _setupSheetRefresh(groupId, config) {
        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
        }

        const timer = setInterval(async () => {
            const sourceId = `csv-${groupId}`;
            if (!this._map.getSource(sourceId)) {
                clearInterval(timer);
                this._refreshTimers.delete(groupId);
                return;
            }

            try {
                const geojson = await this._fetchSheetGeoJSON(config);
                this._map.getSource(sourceId).setData(geojson);
            } catch (error) {
                console.error('Error refreshing sheet layer:', error);
            }
        }, config.refresh);

        this._refreshTimers.set(groupId, timer);
    }

    _setupCSVRefresh(groupId, config) {
        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
        }

        const timer = setInterval(async () => {
            const sourceId = `csv-${groupId}`;
            if (!this._map.getSource(sourceId)) {
                clearInterval(timer);
                this._refreshTimers.delete(groupId);
                return;
            }

            try {
                const response = await fetch(config.url);
                const csvText = await response.text();
                const geojson = this._processCSVData(csvText, config.csvParser);
                this._map.getSource(sourceId).setData(geojson);
            } catch (error) {
                console.error('Error refreshing CSV layer:', error);
            }
        }, config.refresh);

        this._refreshTimers.set(groupId, timer);
    }

    _updateCSVLayerVisibility(groupId, config, visible) {
        if (visible && config.blink) {
            this._setupBlinking(groupId, config);
        } else {
            this._stopBlinking(groupId, config);
        }

        const sourceId = `csv-${groupId}`;
        this._getVariantPrefixes(config).forEach(prefix => {
            const variantSuffix = this._getVariantSuffix(prefix);
            const layers = [
                `${sourceId}-fill${variantSuffix}`,
                `${sourceId}-line${variantSuffix}`,
                `${sourceId}-label${variantSuffix}`,
                `${sourceId}-symbol${variantSuffix}`,
                `${sourceId}-circle${variantSuffix}`,
                `${sourceId}-clusters${variantSuffix}`,
                `${sourceId}-cluster-count${variantSuffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
                }
            });
        });

        if (visible && config.refresh && config.url && !this._refreshTimers.has(groupId)) {
            this._setupCSVRefresh(groupId, config);
        } else if (!visible && this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
            this._refreshTimers.delete(groupId);
        }

        return true;
    }

    _removeCSVLayer(groupId, config) {
        this._stopBlinking(groupId, config);

        const sourceId = `csv-${groupId}`;
        this._getVariantPrefixes(config).forEach(prefix => {
            const variantSuffix = this._getVariantSuffix(prefix);
            const layers = [
                `${sourceId}-fill${variantSuffix}`,
                `${sourceId}-line${variantSuffix}`,
                `${sourceId}-label${variantSuffix}`,
                `${sourceId}-symbol${variantSuffix}`,
                `${sourceId}-circle${variantSuffix}`,
                `${sourceId}-clusters${variantSuffix}`,
                `${sourceId}-cluster-count${variantSuffix}`
            ];

            layers.forEach(layerId => {
                if (this._map.getLayer(layerId)) {
                    this._map.removeLayer(layerId);
                }
            });
        });

        if (this._map.getSource(sourceId)) {
            this._map.removeSource(sourceId);
        }

        return true;
    }

    // Image layer methods
    async _createImageLayer(groupId, config, visible) {
        if (!this._map.getSource(groupId) && visible) {
            if (!config.url || !config.bounds) {
                console.error(`Image layer ${groupId} missing URL or bounds`);
                return false;
            }

            try {
                const url = config.refresh ?
                    (config.url.includes('?') ? `${config.url}&_t=${Date.now()}` : `${config.url}?_t=${Date.now()}`) :
                    config.url;

                await this._loadImage(url);

                const bounds = config.bounds || config.bbox;
                this._map.addSource(groupId, {
                    type: 'image',
                    url: url,
                    coordinates: [
                        [bounds[0], bounds[3]], // top-left
                        [bounds[2], bounds[3]], // top-right
                        [bounds[2], bounds[1]], // bottom-right
                        [bounds[0], bounds[1]]  // bottom-left
                    ],
                    ...(config.attribution && { attribution: config.attribution })
                });

                const layerConfig = this._createLayerConfig({
                    id: groupId,
                    source: groupId,
                    style: {
                        'raster-opacity': config.style?.['raster-opacity'] || config.opacity || 0.85,
                        'raster-fade-duration': 0
                    },
                    visible
                }, 'raster');

                this._addLayerWithSlot(layerConfig, LayerOrderManager.getInsertPosition(this._map, 'img', null, config, this._orderedGroups));

                if (config.refresh) {
                    this._setupImageRefresh(groupId, config);
                }
            } catch (error) {
                console.error(`Failed to load image for layer ${groupId}:`, error);
                return false;
            }
        } else {
            this._updateImageLayerVisibility(groupId, config, visible);
        }

        return true;
    }

    _loadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    }

    /**
     * Set up blinking for a layer
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @private
     */
    _setupBlinking(groupId, config) {
        if (this._blinkTimers.has(groupId)) {
            return;
        }

        const blinkConfig = config.blink;
        if (!blinkConfig || !blinkConfig.condition) {
            return;
        }

        const interval = blinkConfig.interval || 500;
        let isVisible = true;

        const timer = setInterval(() => {
            isVisible = !isVisible;
            const opacity = isVisible ? 1 : 0;

            const layerIds = this.getLayerGroupIds(groupId, config);
            layerIds.forEach(layerId => {
                const layer = this._map.getLayer(layerId);
                if (layer) {
                    const type = layer.type;
                    let opacityProp;

                    switch (type) {
                        case 'fill': opacityProp = 'fill-opacity'; break;
                        case 'line': opacityProp = 'line-opacity'; break;
                        case 'symbol': opacityProp = 'icon-opacity'; break;
                        case 'circle': opacityProp = 'circle-opacity'; break;
                        default: return;
                    }

                    // Get base opacity from config or default to 1
                    const styleType = type === 'symbol' ? 'symbol' : type;
                    const baseOpacity = (config.style && config.style[opacityProp]) !== undefined
                        ? config.style[opacityProp]
                        : 1;

                    const expression = [
                        'case',
                        blinkConfig.condition,
                        opacity === 1 ? baseOpacity : 0,
                        baseOpacity // Default opacity for non-matching features
                    ];

                    try {
                        this._map.setPaintProperty(layerId, opacityProp, expression);

                        // If symbol layer, also handle text-opacity
                        if (type === 'symbol') {
                            const textBaseOpacity = (config.style && config.style['text-opacity']) !== undefined
                                ? config.style['text-opacity']
                                : 1;

                            const textExpression = [
                                'case',
                                blinkConfig.condition,
                                opacity === 1 ? textBaseOpacity : 0,
                                textBaseOpacity
                            ];
                            this._map.setPaintProperty(layerId, 'text-opacity', textExpression);
                        }
                    } catch (e) {
                        // Ignore errors if property is not supported by the layer type
                    }
                }
            });
        }, interval);

        this._blinkTimers.set(groupId, timer);
    }

    /**
     * Stop blinking for a layer
     * @param {string} groupId - Layer group identifier
     * @private
     */
    _stopBlinking(groupId, config = null) {
        if (this._blinkTimers.has(groupId)) {
            clearInterval(this._blinkTimers.get(groupId));
            this._blinkTimers.delete(groupId);

            // Reset opacity for all layers in this group if config is provided
            if (config) {
                const layerIds = this.getLayerGroupIds(groupId, config);
                layerIds.forEach(layerId => {
                    const layer = this._map.getLayer(layerId);
                    if (layer) {
                        const type = layer.type;
                        let opacityProp;

                        switch (type) {
                            case 'fill': opacityProp = 'fill-opacity'; break;
                            case 'line': opacityProp = 'line-opacity'; break;
                            case 'symbol': opacityProp = 'icon-opacity'; break;
                            case 'circle': opacityProp = 'circle-opacity'; break;
                            default: return;
                        }

                        try {
                            // Reset to config style or default (1)
                            const baseOpacity = (config.style && config.style[opacityProp]) !== undefined
                                ? config.style[opacityProp]
                                : 1;
                            this._map.setPaintProperty(layerId, opacityProp, baseOpacity);

                            if (type === 'symbol') {
                                const textBaseOpacity = (config.style && config.style['text-opacity']) !== undefined
                                    ? config.style['text-opacity']
                                    : 1;
                                this._map.setPaintProperty(layerId, 'text-opacity', textBaseOpacity);
                            }
                        } catch (e) { }
                    }
                });
            }
        }
    }

    /**
     * Ensure an icon is loaded into the map
     * @param {string|Array} iconImage - Path or URL to the icon image, or a Mapbox expression
     */
    async _ensureIconLoaded(iconImage) {
        if (!iconImage) return;

        const icons = this._extractIconsFromExpression(iconImage);
        for (const iconPath of icons) {
            if (!this._map.hasImage(iconPath)) {
                try {
                    const image = await this._loadImage(iconPath);
                    if (!this._map.hasImage(iconPath)) {
                        this._map.addImage(iconPath, image);
                    }
                } catch (error) {
                    console.error(`Failed to load icon: ${iconPath}`, error);
                }
            }
        }
    }

    /**
     * Extract all unique icon paths from a Mapbox expression or string
     * @param {string|Array} expression - The icon-image value
     * @returns {Set<string>} - Set of unique icon paths
     * @private
     */
    _extractIconsFromExpression(expression) {
        const icons = new Set();

        const traverse = (expr) => {
            if (typeof expr === 'string') {
                // Only treat as icon path if it looks like a path/URL
                if (expr.includes('/') || expr.includes('.') || expr.startsWith('http')) {
                    icons.add(expr);
                }
            } else if (Array.isArray(expr)) {
                // Skip the first element which is the operator (match, get, case, etc.)
                // Start from index 1 and traverse nested arrays or strings
                for (let i = 1; i < expr.length; i++) {
                    traverse(expr[i]);
                }
            }
        };

        traverse(expression);
        return icons;
    }

    async _loadAndRegisterIconImage(iconUrl) {
        if (!iconUrl || typeof iconUrl !== 'string') return iconUrl;

        if (!iconUrl.includes('/') && !iconUrl.includes('.')) {
            return iconUrl;
        }

        const imageName = iconUrl.split('/').pop().replace(/\.[^/.]+$/, '');

        if (this._map.hasImage(imageName)) {
            return imageName;
        }

        try {
            const img = await this._loadImage(iconUrl);
            this._map.addImage(imageName, img);
            return imageName;
        } catch (error) {
            return iconUrl;
        }
    }

    async _prepareSymbolLayerIcons(style) {
        if (!style || typeof style !== 'object') return style;

        const modifiedStyle = { ...style };

        if (modifiedStyle['icon-image']) {
            const iconImage = modifiedStyle['icon-image'];

            // Handle both simple strings and Mapbox expressions
            if (typeof iconImage === 'string') {
                modifiedStyle['icon-image'] = await this._loadAndRegisterIconImage(iconImage);
            } else if (Array.isArray(iconImage)) {
                // Handle expressions - load all icon paths within the expression
                await this._loadIconsFromExpression(iconImage);
                // Keep the expression as-is, just ensure all referenced icons are loaded
            }
        }

        return modifiedStyle;
    }

    async _loadIconsFromExpression(expression) {
        const traverse = async (expr) => {
            if (Array.isArray(expr)) {
                for (let i = 1; i < expr.length; i++) {
                    if (typeof expr[i] === 'string') {
                        const looksLikeIcon = expr[i].includes('.png') || expr[i].includes('.jpg') ||
                            expr[i].includes('.svg') || expr[i].includes('.jpeg') ||
                            expr[i].includes('.gif') || expr[i].startsWith('http://') ||
                            expr[i].startsWith('https://') || expr[i].startsWith('assets/') ||
                            expr[i].startsWith('data/') || expr[i].startsWith('images/');

                        if (looksLikeIcon) {
                            expr[i] = await this._loadAndRegisterIconImage(expr[i]);
                        }
                    } else if (Array.isArray(expr[i])) {
                        await traverse(expr[i]);
                    }
                }
            }
        };

        await traverse(expression);
    }

    _setupImageRefresh(groupId, config) {
        if (this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
        }

        const timer = setInterval(async () => {
            if (!this._map.getSource(groupId)) {
                clearInterval(timer);
                this._refreshTimers.delete(groupId);
                return;
            }

            try {
                const timestamp = Date.now();
                const url = config.url.includes('?') ?
                    `${config.url}&_t=${timestamp}` :
                    `${config.url}?_t=${timestamp}`;

                await this._loadImage(url);

                const source = this._map.getSource(groupId);
                source.updateImage({
                    url: url,
                    coordinates: source.coordinates
                });
            } catch (error) {
                console.error(`Error refreshing image layer ${groupId}:`, error);
            }
        }, config.refresh);

        this._refreshTimers.set(groupId, timer);
    }

    _updateImageLayerVisibility(groupId, config, visible) {
        if (this._map.getLayer(groupId)) {
            this._map.setLayoutProperty(groupId, 'visibility', visible ? 'visible' : 'none');
        }

        if (visible && config.refresh && !this._refreshTimers.has(groupId)) {
            this._setupImageRefresh(groupId, config);
        } else if (!visible && this._refreshTimers.has(groupId)) {
            clearInterval(this._refreshTimers.get(groupId));
            this._refreshTimers.delete(groupId);
        }

        return true;
    }

    _removeImageLayer(groupId, config) {
        if (this._map.getLayer(groupId)) {
            this._map.removeLayer(groupId);
        }
        if (this._map.getSource(groupId)) {
            this._map.removeSource(groupId);
        }
        return true;
    }

    _updateImageLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        if (this._map.getLayer(groupId)) {
            this._map.setPaintProperty(groupId, 'raster-opacity', finalOpacity);
        }
        return true;
    }

    // Raster style layer methods
    _createRasterStyleLayer(groupId, config, visible) {
        const styleLayerId = config.styleLayer || groupId;

        if (this._map.getLayer(styleLayerId)) {
            this._map.setLayoutProperty(styleLayerId, 'visibility', visible ? 'visible' : 'none');

            if (visible && config.style) {
                this._applyStyleProperties(styleLayerId, config.style);
            }
        } else {
            console.warn(`Style layer '${styleLayerId}' not found in map style`);
            return false;
        }

        return true;
    }

    _applyStyleProperties(layerId, style) {
        const existingLayer = this._map.getLayer(layerId);
        const layerType = existingLayer.type;
        const { paint, layout } = this._categorizeStyleProperties(style, layerType);

        Object.entries(paint).forEach(([property, value]) => {
            try {
                this._map.setPaintProperty(layerId, property, value);
            } catch (error) {
                console.warn(`Failed to set paint property ${property} on layer ${layerId}:`, error);
            }
        });

        Object.entries(layout).forEach(([property, value]) => {
            if (property !== 'visibility') {
                try {
                    this._map.setLayoutProperty(layerId, property, value);
                } catch (error) {
                    console.warn(`Failed to set layout property ${property} on layer ${layerId}:`, error);
                }
            }
        });
    }

    _updateRasterStyleLayerVisibility(groupId, config, visible) {
        return this._createRasterStyleLayer(groupId, config, visible);
    }

    _removeRasterStyleLayer(groupId, config) {
        return this._updateRasterStyleLayerVisibility(groupId, config, false);
    }

    _updateRasterStyleLayerOpacity(groupId, config, opacity) {
        // Apply config.opacity as a multiplier if it exists
        const finalOpacity = (config.opacity !== undefined && config.opacity !== 1)
            ? opacity * config.opacity
            : opacity;

        const styleLayerId = config.styleLayer || groupId;
        if (this._map.getLayer(styleLayerId)) {
            const existingLayer = this._map.getLayer(styleLayerId);
            if (existingLayer.type === 'raster') {
                this._map.setPaintProperty(styleLayerId, 'raster-opacity', finalOpacity);
            }
        }
        return true;
    }

    // Layer group toggle methods
    _createLayerGroupToggle(groupId, config, visible) {
        if (config.groups) {
            config.groups.forEach(subGroup => {
                const allLayers = this._map.getStyle().layers
                    .map(layer => layer.id)
                    .filter(id =>
                        id === subGroup.id ||
                        id.startsWith(`${subGroup.id}-`) ||
                        id.startsWith(`${subGroup.id} `)
                    );
                this._updateLayerVisibility(allLayers, visible);
            });
        }
        return true;
    }

    _updateLayerGroupToggleVisibility(groupId, config, visible) {
        return this._createLayerGroupToggle(groupId, config, visible);
    }

    _updateLayerVisibility(layers, isVisible) {
        layers.forEach(layerId => {
            if (this._map.getLayer(layerId)) {
                this._map.setLayoutProperty(
                    layerId,
                    'visibility',
                    isVisible ? 'visible' : 'none'
                );
            }
        });
    }

    /**
     * Get all layers associated with a layer group
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @returns {Array} - Array of layer IDs
     */
    getLayerGroupIds(groupId, config) {
        const variantPrefixes = this._getVariantPrefixes(config);

        switch (config.type) {
            case 'style':
                if (config.layers) {
                    const styleLayers = this._map.getStyle().layers;
                    return config.layers.flatMap(layer => {
                        return styleLayers
                            .filter(styleLayer => styleLayer['source-layer'] === layer.sourceLayer)
                            .map(styleLayer => styleLayer.id);
                    });
                }
                return [];
            case 'vector':
                return variantPrefixes.flatMap(prefix => {
                    const suffix = this._getVariantSuffix(prefix);
                    return [
                        `vector-layer-${groupId}${suffix}`,
                        `vector-layer-${groupId}-outline${suffix}`,
                        `vector-layer-${groupId}-circle${suffix}`,
                        `vector-layer-${groupId}-text${suffix}`
                    ];
                }).filter(id => this._map.getLayer(id));
            case 'tms':
                return [`tms-layer-${groupId}`].filter(id => this._map.getLayer(id));
            case 'cog':
                return [`cog-layer-${groupId}`].filter(id => this._map.getLayer(id));
            case 'wmts':
                return [`wmts-layer-${groupId}`].filter(id => this._map.getLayer(id));
            case 'wms':
                return [`wms-layer-${groupId}`].filter(id => this._map.getLayer(id));
            case 'overpass':
            case 'js':
            case 'geojson': {
                const sourceId = `geojson-${groupId}`;

                if (config.clusterSeparateBy) {
                    const cache = this._layerCache.get(groupId);
                    if (cache && cache.subSources) {
                        return cache.subSources.flatMap(subSourceId => {
                            const segregateSuffix = subSourceId.replace(`geojson-${groupId}-`, '');
                            return variantPrefixes.flatMap(prefix => {
                                const variantSuffix = this._getVariantSuffix(prefix);
                                const idSuffix = `-${segregateSuffix}${variantSuffix}`;
                                return [
                                    `${subSourceId}-fill${idSuffix}`,
                                    `${subSourceId}-line${idSuffix}`,
                                    `${subSourceId}-label${idSuffix}`,
                                    `${subSourceId}-symbol${idSuffix}`,
                                    `${subSourceId}-circle${idSuffix}`,
                                    `${subSourceId}-clusters${idSuffix}`,
                                    `${subSourceId}-cluster-count${idSuffix}`
                                ];
                            });
                        }).filter(id => this._map.getLayer(id));
                    }
                }

                return variantPrefixes.flatMap(prefix => {
                    const variantSuffix = this._getVariantSuffix(prefix);
                    return [
                        `${sourceId}-fill${variantSuffix}`,
                        `${sourceId}-line${variantSuffix}`,
                        `${sourceId}-label${variantSuffix}`,
                        `${sourceId}-symbol${variantSuffix}`,
                        `${sourceId}-circle${variantSuffix}`,
                        `${sourceId}-clusters${variantSuffix}`,
                        `${sourceId}-cluster-count${variantSuffix}`
                    ];
                }).filter(id => this._map.getLayer(id));
            }
            case 'csv':
            case 'sheet': {
                const sourceId = `csv-${groupId}`;
                return variantPrefixes.flatMap(prefix => {
                    const variantSuffix = this._getVariantSuffix(prefix);
                    return [
                        `${sourceId}-fill${variantSuffix}`,
                        `${sourceId}-line${variantSuffix}`,
                        `${sourceId}-label${variantSuffix}`,
                        `${sourceId}-symbol${variantSuffix}`,
                        `${sourceId}-circle${variantSuffix}`,
                        `${sourceId}-clusters${variantSuffix}`,
                        `${sourceId}-cluster-count${variantSuffix}`
                    ];
                }).filter(id => this._map.getLayer(id));
            }
            case 'img':
            case 'raster-style-layer':
                return [config.styleLayer || groupId];
            default:
                return [];
        }
    }

    /**
     * Check if a layer group exists on the map
     * @param {string} groupId - Layer group identifier
     * @param {Object} config - Layer configuration
     * @returns {boolean} - True if layer group exists
     */
    hasLayerGroup(groupId, config) {
        const layerIds = this.getLayerGroupIds(groupId, config);
        return layerIds.length > 0;
    }

    /**
     * Categorize style properties into paint and layout based on layer type
     * @param {Object} style - Style object with mixed paint/layout properties
     * @param {string} layerType - The layer type (e.g., 'raster', 'fill', 'line')
     * @returns {Object} - Object with separate paint and layout properties
     */
    _categorizeStyleProperties(style, layerType) {
        if (!style || typeof style !== 'object') {
            return { paint: {}, layout: {} };
        }

        const paint = {};
        const layout = {};

        // Get property lists for this layer type
        const layoutProps = [
            ...(this._stylePropertyMapping.layout.common || []),
            ...(this._stylePropertyMapping.layout[layerType] || [])
        ];
        const paintProps = this._stylePropertyMapping.paint[layerType] || [];

        // Categorize each property in the style object
        Object.keys(style).forEach(property => {
            // First check if this property is valid for this layer type
            const isValidForLayerType = this._isPropertyValidForLayerType(property, layerType);
            if (!isValidForLayerType) {
                // Skip invalid properties completely - don't add them to either paint or layout
                return;
            }

            if (layoutProps.includes(property)) {
                layout[property] = style[property];
            } else if (paintProps.includes(property)) {
                paint[property] = style[property];
            } else {
                // If property is not in our mapping, make an educated guess
                // Most properties are paint properties, layout properties are fewer
                if (property === 'visibility' || property.includes('-sort-key') ||
                    property.includes('-placement') || property.includes('-anchor') ||
                    property.includes('-field') || property.includes('-font') ||
                    property.includes('-size') || property.includes('-image') ||
                    property.includes('-cap') || property.includes('-join') ||
                    property.includes('-allow-overlap') || property.includes('-keep-upright') ||
                    property.includes('-writing-mode') || property.includes('-transform') ||
                    property.includes('-offset') || property.includes('-alignment') ||
                    property.includes('-justify') || property.includes('-line-height') ||
                    property.includes('-max-width') || property.includes('-variable-anchor') ||
                    property.includes('-radial-offset') || property.includes('-padding')) {
                    layout[property] = style[property];
                } else {
                    paint[property] = style[property];
                }
            }
        });

        return { paint, layout };
    }

    /**
     * Check if a property is valid for a given layer type
     * @param {string} property - The property name
     * @param {string} layerType - The layer type (fill, line, symbol, circle, etc.)
     * @returns {boolean} - True if property is valid for this layer type
     */
    _isPropertyValidForLayerType(property, layerType) {
        // Define invalid property patterns for each layer type
        // Note: text- properties are NOT filtered out because text layers are created separately as symbol type
        const invalidPatterns = {
            symbol: [
                /^fill-/,        // fill-color, fill-opacity, etc.
                /^line-/,        // line-color, line-width, etc.
                /^circle-/       // circle-radius, circle-color, etc.
            ],
            fill: [
                /^fill-extrusion-/, // fill-extrusion-height, fill-extrusion-color, etc.
                /^line-/,        // line-color, line-width, etc.
                /^icon-/,        // icon-image, icon-color, etc.
                /^circle-/,      // circle-radius, circle-color, etc.
                /^text-/,        // text-field, text-color, etc.
                /^symbol-/       // symbol-sort-key, etc.
            ],
            'fill-extrusion': [
                /^fill-(?!extrusion)/, // fill-color, fill-opacity, etc. but not fill-extrusion-*
                /^line-/,        // line-color, line-width, etc.
                /^icon-/,        // icon-image, icon-color, etc.
                /^circle-/,      // circle-radius, circle-color, etc.
                /^text-/,        // text-field, text-color, etc.
                /^symbol-/       // symbol-sort-key, etc.
            ],
            line: [
                /^fill-/,        // fill-color, fill-opacity, etc.
                /^icon-/,        // icon-image, icon-color, etc.
                /^circle-/,      // circle-radius, circle-color, etc.
                /^text-/,        // text-field, text-color, etc.
                /^symbol-/       // symbol-sort-key, etc.
            ],
            circle: [
                /^fill-/,        // fill-color, fill-opacity, etc.
                /^line-/,        // line-color, line-width, etc.
                /^icon-/,        // icon-image, icon-color, etc.
                /^text-/,        // text-field, text-color, etc.
                /^symbol-/       // symbol-sort-key, etc.
            ]
        };

        const patterns = invalidPatterns[layerType];
        if (!patterns) {
            // For unknown layer types, be permissive
            return true;
        }

        // Check if property matches any invalid pattern
        return !patterns.some(pattern => pattern.test(property));
    }

    /**
     * Filter style properties to only include those valid for the specified layer type
     * @param {Object} style - The complete style object
     * @param {string} layerType - The layer type (fill, line, symbol, circle, etc.)
     * @returns {Object} - Filtered style object
     */
    _filterStyleForLayerType(style, layerType) {
        if (!style || typeof style !== 'object') {
            return {};
        }

        const filteredStyle = {};

        Object.keys(style).forEach(property => {
            if (this._isPropertyValidForLayerType(property, layerType)) {
                filteredStyle[property] = style[property];
            }
        });

        return filteredStyle;
    }

    /**
     * Add a layer to the map with proper slot or position handling
     * Reference: https://docs.mapbox.com/mapbox-gl-js/api/map/#map#addlayer
     * @param {Object} layerConfig - Layer configuration object
     * @param {string|null} insertPosition - Slot name ('bottom', 'middle', 'top') or layer ID to insert before
     */
    _addLayerWithSlot(layerConfig, insertPosition) {
        // Check if insertPosition is a slot name
        const slotNames = ['bottom', 'middle', 'top'];

        if (insertPosition && slotNames.includes(insertPosition)) {
            // Use slot-based insertion
            // Reference: https://docs.mapbox.com/style-spec/reference/layers/#layer-properties
            layerConfig.slot = insertPosition;
            this._map.addLayer(layerConfig);
        } else if (insertPosition) {
            // Use traditional beforeId insertion
            this._map.addLayer(layerConfig, insertPosition);
        } else {
            // No position specified, append to end
            this._map.addLayer(layerConfig);
        }
    }

    /**
     * Create layer configuration with properly categorized paint/layout properties
     * @param {Object} config - Layer configuration
     * @param {string} layerType - The layer type
     * @returns {Object} - Layer configuration with separated paint/layout
     */
    _createLayerConfig(config, layerType) {
        // Secondary style variants are pure overlays — they should not inherit
        // defaults from `_defaults.json` (only the base variant does).
        const applyDefaults = config.applyDefaults !== false;
        const defaultStyles = applyDefaults ? this._getDefaultStylesForLayerType(layerType) : {};

        // Intelligently merge user styles with defaults (preserving feature-state logic)
        const mergedStyles = this._intelligentStyleMerge(config.style || {}, defaultStyles);

        const { paint, layout } = this._categorizeStyleProperties(mergedStyles, layerType);

        const layerConfig = {
            id: config.id,
            type: layerType,
            source: config.source,
            layout: {
                visibility: config.initiallyChecked !== false ? 'visible' : 'none',
                ...layout
            },
            paint: paint
        };

        // Add optional properties
        if (config['source-layer']) {
            layerConfig['source-layer'] = config['source-layer'];
        }
        if (config.filter) {
            layerConfig.filter = config.filter;
        }
        // Always add metadata for layer ordering, merging with any existing metadata
        layerConfig.metadata = {
            ...(config.metadata || {}),
            groupId: config.groupId || config.id,
            layerType: layerType
        };
        if (config.minzoom !== undefined) {
            layerConfig.minzoom = config.minzoom;
        }
        if (config.maxzoom !== undefined) {
            layerConfig.maxzoom = config.maxzoom;
        }

        return layerConfig;
    }

    /**
     * Get default styles for a specific layer type
     * @param {string} layerType - The layer type (fill, line, symbol, circle, etc.)
     * @returns {Object} - Default styles for this layer type
     */
    _getDefaultStylesForLayerType(layerType) {
        if (!this._defaultStyles || !this._defaultStyles.vector) {
            return {};
        }

        // Map layer types to default style categories
        const styleMap = {
            'fill': this._defaultStyles.vector.fill || {},
            'fill-extrusion': this._defaultStyles.vector['fill-extrusion'] || {},
            'line': this._defaultStyles.vector.line || {},
            'symbol': this._defaultStyles.vector.text || {},
            'circle': this._defaultStyles.vector.circle || {},
            'raster': this._defaultStyles.raster || {}
        };

        return styleMap[layerType] || {};
    }

    /**
     * Intelligently combine user color with default style expression (preserving feature-state logic)
     * @param {*} userColor - User-provided color value
     * @param {*} defaultStyleExpression - Default style expression (may contain feature-state logic)
     * @returns {*} - Combined style expression
     */
    _combineWithDefaultStyle(userColor, defaultStyleExpression) {
        // If no user color is provided, return the default style unchanged
        if (!userColor) return defaultStyleExpression;

        // If default style is not an expression (just a simple color), return user color
        if (!Array.isArray(defaultStyleExpression)) return userColor;

        // If user color contains a zoom expression (interpolate/step with zoom), use it directly
        if (Array.isArray(userColor) && this._hasZoomExpression(userColor)) {
            return userColor;
        }

        // Clone the default style expression to avoid modifying the original
        const result = JSON.parse(JSON.stringify(defaultStyleExpression));

        // Handle different types of expressions
        if (result[0] === 'case') {
            // Simple case expression - replace the fallback color (last value)
            result[result.length - 1] = userColor;
        } else if (result[0] === 'interpolate' && result[2] && Array.isArray(result[2]) && result[2][0] === 'zoom') {
            // Interpolate expression with zoom - replace fallback colors in nested case expressions
            this._replaceColorsInInterpolateExpression(result, userColor);
        } else {
            // For other expression types, return user color directly
            return userColor;
        }

        return result;
    }

    /**
     * Check if an expression contains zoom-based logic
     * @param {*} expression - The expression to check
     * @returns {boolean} - True if expression has zoom logic
     */
    _hasZoomExpression(expression) {
        if (!Array.isArray(expression)) return false;

        // Check if this is an interpolate or step expression with zoom
        if ((expression[0] === 'interpolate' || expression[0] === 'step') &&
            expression.length > 2 &&
            Array.isArray(expression[2]) &&
            expression[2][0] === 'zoom') {
            return true;
        }

        // Recursively check nested expressions
        for (let i = 1; i < expression.length; i++) {
            if (Array.isArray(expression[i]) && this._hasZoomExpression(expression[i])) {
                return true;
            }
        }

        return false;
    }

    /**
     * Replace colors in interpolate expressions while preserving structure
     * @param {Array} interpolateExpr - The interpolate expression to modify
     * @param {*} newColor - The new color to use
     */
    _replaceColorsInInterpolateExpression(interpolateExpr, newColor) {
        // For interpolate expressions like: ["interpolate", ["linear"], ["zoom"], 6, caseExpr1, 16, caseExpr2]
        // We need to replace the fallback color in each case expression
        for (let i = 4; i < interpolateExpr.length; i += 2) {
            const valueExpr = interpolateExpr[i];
            if (Array.isArray(valueExpr) && valueExpr[0] === 'case') {
                // Replace the fallback color (last value) in the case expression
                valueExpr[valueExpr.length - 1] = newColor;
            }
        }
    }

    /**
     * Intelligently merge user styles with default styles
     * @param {Object} userStyles - User-provided styles
     * @param {Object} defaultStyles - Default styles with feature-state logic
     * @returns {Object} - Merged styles
     */
    _intelligentStyleMerge(userStyles, defaultStyles) {
        if (!defaultStyles || typeof defaultStyles !== 'object') {
            return userStyles || {};
        }
        if (!userStyles || typeof userStyles !== 'object') {
            return defaultStyles;
        }

        const mergedStyles = {};

        // First, add all default styles
        Object.keys(defaultStyles).forEach(property => {
            mergedStyles[property] = defaultStyles[property];
        });

        // Then, intelligently merge user styles
        Object.keys(userStyles).forEach(property => {
            const userValue = userStyles[property];
            const defaultValue = defaultStyles[property];

            // For color properties, use intelligent combining
            if (property.includes('-color') && defaultValue) {
                mergedStyles[property] = this._combineWithDefaultStyle(userValue, defaultValue);
            } else {
                // For non-color properties, user value takes precedence
                mergedStyles[property] = userValue;
            }
        });

        // Special handling for text-halo-color: use fill-color as fallback if text-halo-color not provided
        if (!userStyles['text-halo-color'] && userStyles['fill-color'] && defaultStyles['text-halo-color']) {
            mergedStyles['text-halo-color'] = this._combineWithDefaultStyle(
                userStyles['fill-color'],
                defaultStyles['text-halo-color']
            );
        }

        return mergedStyles;
    }

    /**
     * Update line layers with dynamic sort keys for hover/selection z-ordering
     * @param {Set} selectedFeatureIds - Set of selected feature IDs
     * @param {Set} hoveredFeatureIds - Set of hovered feature IDs
     */
    updateLineLayerSortKeys(selectedFeatureIds, hoveredFeatureIds) {
        const style = this.getStyle();
        if (!style || !style.layers) return;

        const selectedIds = Array.from(selectedFeatureIds);
        const hoveredIds = Array.from(hoveredFeatureIds);

        // Skip when nothing changed — setLayoutProperty triggers a worker relayout
        // of the line geometry, so re-applying an identical sort key is pure cost.
        const signature = `${selectedIds.join(',')}|${hoveredIds.join(',')}`;
        if (signature === this._lastSortKeySignature) return;
        this._lastSortKeySignature = signature;

        // Defer the relayout to the next frame. Changing line-sort-key forces
        // Mapbox to re-tessellate every line-outline layer on the worker, which
        // blocks the next WebGL repaint — on mobile this delays selection markers
        // and the selection layer by ~1s. Deferring lets the selection visuals
        // paint first; the z-ordering settles a frame later.
        if (this._sortKeyRAF) cancelAnimationFrame(this._sortKeyRAF);
        this._sortKeyRAF = requestAnimationFrame(() => {
            this._sortKeyRAF = null;
            style.layers.forEach(layer => {
                if (layer.type === 'line' && layer.id.includes('-outline')) {
                    const sortKeyExpression = [
                        'case',
                        ['in', ['id'], ['literal', selectedIds]], 3,
                        ['in', ['id'], ['literal', hoveredIds]], 1,
                        2
                    ];

                    try {
                        this._map.setLayoutProperty(layer.id, 'line-sort-key', sortKeyExpression);
                    } catch (error) {
                        console.warn(`Failed to update sort key for ${layer.id}:`, error);
                    }
                }
            });
        });
    }

    /**
     * Cleanup all layer groups and dispose of the API
     */
    cleanup() {
        // Clean up time change event listener
        if (this._timeChangeHandler) {
            try {
                this._map.getContainer().removeEventListener('timechange', this._timeChangeHandler);
                window.removeEventListener('timechange', this._timeChangeHandler);
            } catch (error) {
                console.warn('Failed to remove time change event listener:', error);
            }
            this._timeChangeHandler = null;
        }

        // Clean up all event listeners
        this._eventListeners.forEach((listeners, eventType) => {
            listeners.forEach(listener => {
                try {
                    this._map.off(eventType, listener);
                } catch (error) {
                    console.warn(`Failed to remove event listener for ${eventType}:`, error);
                }
            });
        });

        // Clear all refresh timers
        this._refreshTimers.forEach(timer => clearInterval(timer));
        this._refreshTimers.clear();

        // Clear caches
        this._layerCache.clear();
        this._sourceCache.clear();
        this._timeBasedLayers.clear();

        // Clean up all layer groups
        this._layerGroups.forEach((groupData, groupId) => {
            this.removeLayerGroup(groupId, groupData.config);
        });

        this._layerGroups.clear();
        this._eventListeners.clear();
        this._map = null;
    }

    // ===========================================
    // MAP QUERY AND INTERACTION METHODS
    // ===========================================

    /**
     * Query rendered features at a point or within a geometry
     * @param {Array|Object} pointOrGeometry - Point [x, y] or geometry object
     * @param {Object} options - Query options
     * @returns {Array} Array of features
     */
    queryRenderedFeatures(pointOrGeometry, options = {}) {
        try {
            return this._map.queryRenderedFeatures(pointOrGeometry, options);
        } catch (error) {
            // Handle DEM data range errors gracefully
            if (error.message && error.message.includes('out of range source coordinates for DEM data')) {
                return [];
            } else {
                // Re-throw other errors as they might be more serious
                throw error;
            }
        }
    }

    /**
     * Get current map zoom level
     * @returns {number} Current zoom level
     */
    getZoom() {
        return this._map.getZoom();
    }

    /**
     * Get current map center
     * @returns {LngLat} Current center coordinates
     */
    getCenter() {
        return this._map.getCenter();
    }

    /**
     * Ease map to a location with options
     * @param {Object} options - Ease options (center, zoom, duration, offset, etc.)
     */
    easeTo(options) {
        return this._map.easeTo(options);
    }

    /**
     * Fly to a location with options
     * @param {Object} options - Fly options (center, zoom, duration, etc.)
     */
    flyTo(options) {
        return this._map.flyTo(options);
    }

    // ===========================================
    // MAP STYLE AND LAYER INSPECTION METHODS
    // ===========================================

    /**
     * Get the current map style
     * @returns {Object} Current style object
     */
    getStyle() {
        return this._map.getStyle();
    }

    /**
     * Get a specific layer by ID
     * @param {string} layerId - Layer ID
     * @returns {Object|null} Layer object or null if not found
     */
    getLayer(layerId) {
        return this._map.getLayer(layerId);
    }

    /**
     * Get paint property value for a layer
     * @param {string} layerId - Layer ID
     * @param {string} property - Paint property name
     * @returns {*} Property value
     */
    getPaintProperty(layerId, property) {
        return this._map.getPaintProperty(layerId, property);
    }

    /**
     * Get layout property value for a layer
     * @param {string} layerId - Layer ID
     * @param {string} property - Layout property name
     * @returns {*} Property value
     */
    getLayoutProperty(layerId, property) {
        return this._map.getLayoutProperty(layerId, property);
    }

    /**
     * Set paint property for a layer
     * @param {string} layerId - Layer ID
     * @param {string} property - Paint property name
     * @param {*} value - Property value
     */
    setPaintProperty(layerId, property, value) {
        return this._map.setPaintProperty(layerId, property, value);
    }

    /**
     * Set layout property for a layer
     * @param {string} layerId - Layer ID
     * @param {string} property - Layout property name
     * @param {*} value - Property value
     */
    setLayoutProperty(layerId, property, value) {
        return this._map.setLayoutProperty(layerId, property, value);
    }

    // ===========================================
    // FEATURE STATE MANAGEMENT METHODS
    // ===========================================

    /**
     * Set feature state for a specific feature
     * @param {Object} feature - Feature identifier with source and sourceLayer
     * @param {Object} state - State object to set
     */
    setFeatureState(feature, state) {
        return this._map.setFeatureState(feature, state);
    }

    /**
     * Remove feature state for a specific feature
     * @param {Object} feature - Feature identifier with source and sourceLayer
     * @param {string} stateKey - Optional state key to remove (removes all if not specified)
     */
    removeFeatureState(feature, stateKey = null) {
        if (stateKey) {
            return this._map.removeFeatureState(feature, stateKey);
        } else {
            return this._map.removeFeatureState(feature);
        }
    }

    /**
     * Get feature state for a specific feature
     * @param {Object} feature - Feature identifier with source and sourceLayer
     * @returns {Object} Current feature state
     */
    getFeatureState(feature) {
        return this._map.getFeatureState(feature);
    }

    // ===========================================
    // EVENT HANDLING METHODS
    // ===========================================

    /**
     * Add event listener to the map
     * @param {string} type - Event type
     * @param {Function} listener - Event listener function
     * @param {Object} layerIdOrOptions - Layer ID or options object
     */
    on(type, listener, layerIdOrOptions) {
        // Store the listener for cleanup
        if (!this._eventListeners.has(type)) {
            this._eventListeners.set(type, new Set());
        }
        this._eventListeners.get(type).add(listener);

        if (layerIdOrOptions) {
            return this._map.on(type, layerIdOrOptions, listener);
        } else {
            return this._map.on(type, listener);
        }
    }

    /**
     * Remove event listener from the map
     * @param {string} type - Event type
     * @param {Function} listener - Event listener function
     * @param {Object} layerIdOrOptions - Layer ID or options object
     */
    off(type, listener, layerIdOrOptions) {
        // Remove from our tracking
        if (this._eventListeners.has(type)) {
            this._eventListeners.get(type).delete(listener);
        }

        if (layerIdOrOptions) {
            return this._map.off(type, layerIdOrOptions, listener);
        } else {
            return this._map.off(type, listener);
        }
    }

    /**
     * Add one-time event listener to the map
     * @param {string} type - Event type
     * @param {Function} listener - Event listener function
     * @param {Object} layerIdOrOptions - Layer ID or options object
     */
    once(type, listener, layerIdOrOptions) {
        const wrappedListener = (...args) => {
            // Remove from our tracking when it fires
            if (this._eventListeners.has(type)) {
                this._eventListeners.get(type).delete(wrappedListener);
            }
            return listener(...args);
        };

        // Store the wrapped listener for cleanup
        if (!this._eventListeners.has(type)) {
            this._eventListeners.set(type, new Set());
        }
        this._eventListeners.get(type).add(wrappedListener);

        if (layerIdOrOptions) {
            return this._map.once(type, layerIdOrOptions, wrappedListener);
        } else {
            return this._map.once(type, wrappedListener);
        }
    }

    // ===========================================
    // CONTAINER AND DOM METHODS
    // ===========================================

    /**
     * Get the map container element
     * @returns {HTMLElement} Map container
     */
    getContainer() {
        return this._map.getContainer();
    }

    /**
     * Get the map canvas element
     * @returns {HTMLCanvasElement} Map canvas
     */
    getCanvas() {
        return this._map.getCanvas();
    }

    // ===========================================
    // UTILITY METHODS
    // ===========================================

    /**
     * Check if a layer exists on the map
     * @param {string} layerId - Layer ID to check
     * @returns {boolean} True if layer exists
     */
    hasLayer(layerId) {
        return !!this._map.getLayer(layerId);
    }

    /**
     * Check if a source exists on the map
     * @param {string} sourceId - Source ID to check
     * @returns {boolean} True if source exists
     */
    hasSource(sourceId) {
        return !!this._map.getSource(sourceId);
    }

    /**
     * Get a source by ID
     * @param {string} sourceId - Source ID
     * @returns {Object|null} Source object or null if not found
     */
    getSource(sourceId) {
        return this._map.getSource(sourceId);
    }

    /**
     * Get all layer IDs that match a specific pattern or criteria
     * @param {string|RegExp|Function} matcher - String pattern, RegExp, or function to match layers
     * @returns {Array} Array of matching layer IDs
     */
    getMatchingLayerIds(matcher) {
        const style = this.getStyle();
        if (!style.layers) return [];

        return style.layers
            .filter(layer => {
                if (typeof matcher === 'string') {
                    return layer.id.includes(matcher);
                } else if (matcher instanceof RegExp) {
                    return matcher.test(layer.id);
                } else if (typeof matcher === 'function') {
                    return matcher(layer);
                }
                return false;
            })
            .map(layer => layer.id);
    }

    /**
     * Batch update multiple paint properties for a layer
     * @param {string} layerId - Layer ID
     * @param {Object} properties - Object with property names as keys and values as values
     */
    batchSetPaintProperties(layerId, properties) {
        Object.entries(properties).forEach(([property, value]) => {
            this.setPaintProperty(layerId, property, value);
        });
    }

    /**
     * Batch update multiple layout properties for a layer
     * @param {string} layerId - Layer ID
     * @param {Object} properties - Object with property names as keys and values as values
     */
    batchSetLayoutProperties(layerId, properties) {
        Object.entries(properties).forEach(([property, value]) => {
            this.setLayoutProperty(layerId, property, value);
        });
    }
} 